import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react';
import ReactDOM from 'react-dom/client';
import L, {markerClusterGroup} from 'leaflet';
import {formatEventListTimeParts, isTimeUnknown, LANG_EN, LANG_RU} from './dateFormatters';
import {getCurrentLanguage} from './cookieUtils';
import {setupThemeSwitching, cartoTileUrl, CARTO_ATTRIBUTION} from './mapUtils';
import SubmitEventModal from './submitEventModal';
import ClusterZoomAnimator from './clusterZoomAnimator';

console.log('[homeMap] bundle loaded (SSR panel, hasSSRPanel from bottomPanel)');

const BBOX_EPS = 1e-9;
const DEFAULT_LIST_BATCH_SIZE = 50;
const bboxContains = (cl, lat, lon) =>
    lat >= cl['lat_min'] - BBOX_EPS && lat <= cl['lat_max'] + BBOX_EPS &&
    lon >= cl['lon_min'] - BBOX_EPS && lon <= cl['lon_max'] + BBOX_EPS;

const escapeHtml = (str) => {
    if (str == null || typeof str !== 'string') return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
};

const escapeAttr = (str) => {
    if (str == null || typeof str !== 'string') return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
};

const LIST_SKELETON_HTML = Array.from({length: 5}).map(() =>
    '<div class="skeleton-item"><div class="skeleton-time"></div><div class="skeleton-title"></div><div class="skeleton-distance"></div></div>'
).join('');

const buildResultItemHtml = (event, {lang, href, kmLabel}) => {
    const isOngoing = event && 'is_ongoing' in event ? event['is_ongoing'] : false;
    const timeIcon = isOngoing ? 'fa-play-circle' : 'fa-clock';
    const timeParts = formatEventListTimeParts(event, lang);
    let timePrimary = timeParts.primary;
    const timeVenue = timeParts.venue;
    if (!timePrimary && event && event.display_time) timePrimary = event.display_time;
    const name = escapeHtml(event && event.name ? event.name : '');
    const lat = event && event.latitude != null ? String(event.latitude) : '';
    const lng = event && event.longitude != null ? String(event.longitude) : '';
    const isOngoingAttr = isOngoing ? 'true' : 'false';
    const tzOffsetStr = event && event.timezone_offset != null ? String(event.timezone_offset) : '0';
    const distanceBlock = event.distance !== undefined
        ? `<div class="result-distance"><i class="fas fa-route"></i><span class="result-distance__value">${Number(event.distance).toFixed(1)} ${kmLabel}</span></div>`
        : '';
    const venueHtml = timeVenue ? `<span class="result-time-venue">${escapeHtml(timeVenue)}</span>` : '';
    return `<a class="result-item" href="${escapeAttr(href)}" data-latitude="${escapeAttr(lat)}" data-longitude="${escapeAttr(lng)}" data-timezone-offset="${escapeAttr(tzOffsetStr)}" data-is-ongoing="${isOngoingAttr}"><div class="result-time"><i class="fas ${timeIcon}"></i><div class="result-time-text"><span class="result-time-primary">${escapeHtml(timePrimary)}</span>${venueHtml}</div></div><h3 class="result-title">${name}</h3>${distanceBlock}</a>`;
};

const matchesRenderedMarkup = (listItemsEl, events, count, hrefOf) => {
    const links = listItemsEl.querySelectorAll('a.result-item');
    if (events.length < count || links.length !== count) return false;
    for (let index = 0; index < count; index += 1) {
        if (links[index].getAttribute('href') !== (hrefOf(events[index]) || '#')) return false;
    }
    return true;
};

const isRenderedPrefixOf = (rendered, events) =>
    rendered !== null
    && rendered.length <= events.length
    && rendered.every((event, index) => event === events[index]);

const getClusterKey = (cluster) =>
    `${cluster['lat_min']}_${cluster['lat_max']}_${cluster['lon_min']}_${cluster['lon_max']}_${cluster['cnt']}`;

const MAP_VIEWPORT_PAD = 1;
const MAP_FETCH_DEBOUNCE_MS = 250;
const MAP_ZOOM_FETCH_DEBOUNCE_MS = 400;

const IP_LOCATION_ACCURACY_METERS = {
    ip_geolocation: 10000,
    ip_record: 15000,
    ip_database: 15000,
    ip_mask_class_c: 30000,
};
const DEFAULT_IP_LOCATION_ACCURACY_METERS = 15000;

const isIpLocationSource = (source) =>
    typeof source === 'string' && (source.startsWith('ip_') || source === 'fallback');

const getIpLocationAccuracyMeters = (source) =>
    IP_LOCATION_ACCURACY_METERS[source] || DEFAULT_IP_LOCATION_ACCURACY_METERS;

const createPreciseLocationMarker = (latitude, longitude) => L.marker([latitude, longitude], {
    icon: L.divIcon({
        className: 'user-location',
        html: '<div style="width: 28px; height: 28px; background: #FF6B6B; border: 4px solid white; border-radius: 50%; box-shadow: 0 2px 10px rgba(0,0,0,0.8);"></div>',
        iconSize: [28, 28]
    })
});

const createApproximateLocationLayer = (latitude, longitude, radiusMeters, label) => {
    const area = L.circle([latitude, longitude], {
        radius: radiusMeters,
        className: 'user-location-approx',
        interactive: false
    });
    const dot = L.marker([latitude, longitude], {
        icon: L.divIcon({
            className: 'user-location-approx-dot',
            html: '<div></div>',
            iconSize: [14, 14]
        }),
        keyboard: false
    });
    if (label) {
        dot.bindTooltip(label);
    }
    return L.layerGroup([area, dot]);
};

const getViewSignature = (map) => {
    const center = map.getCenter();
    return `${Math.round(map.getZoom())}_${center.lat.toFixed(6)}_${center.lng.toFixed(6)}`;
};

const findContainingCluster = (clusters, lat, lon) => {
    let best = null;
    let bestDistance = Infinity;
    clusters.forEach(cl => {
        if (!bboxContains(cl, lat, lon)) return;
        const dLat = cl['center_lat'] - lat;
        const dLon = cl['center_lon'] - lon;
        const distance = dLat * dLat + dLon * dLon;
        if (distance < bestDistance) {
            bestDistance = distance;
            best = cl;
        }
    });
    return best;
};

const collectClusterLayerDiff = (clustersLayer, mapClusters) => {
    const existingClusterMarkers = new Map();
    clustersLayer.eachLayer(layer => {
        if (layer && layer['_cluster']) existingClusterMarkers.set(getClusterKey(layer['_cluster']), layer);
    });
    const newClusterKeys = new Set(mapClusters.map(getClusterKey));
    const removedClusters = [];
    existingClusterMarkers.forEach((marker, key) => {
        if (!newClusterKeys.has(key)) removedClusters.push(marker['_cluster']);
    });
    const addedClusters = mapClusters.filter(cl => !existingClusterMarkers.has(getClusterKey(cl)));
    return {existingClusterMarkers, addedClusters, removedClusters};
};

const syncEventMarkersLayer = ({eventsLayer, mapMarkers, direction, removedClusters, addedClusters, animator, createMarker, refreshPopup}) => {
    const existingEventMarkers = new Map();
    eventsLayer.eachLayer(layer => {
        if (layer && layer['_eventKey']) existingEventMarkers.set(layer['_eventKey'], layer);
    });
    const seenEventKeys = new Set();
    mapMarkers.forEach(ev => {
        const key = getEventMarkerKey(ev);
        const reused = key && existingEventMarkers.get(key);
        if (reused) {
            if (animator && animator.isRetiring(reused)) animator.resurrect(reused);
            ev._marker = reused;
            reused['_eventData'] = ev;
            const ll = reused.getLatLng();
            if (ll.lat !== ev['latitude'] || ll.lng !== ev['longitude']) {
                reused.setLatLng([ev['latitude'], ev['longitude']]);
            }
            refreshPopup(reused, ev);
            seenEventKeys.add(key);
            return;
        }
        const marker = createMarker(ev);
        if (animator && direction === 'in') {
            const parent = findContainingCluster(removedClusters, ev['latitude'], ev['longitude']);
            if (parent) animator.spawnFromParent(marker, [parent['center_lat'], parent['center_lon']]);
        }
        eventsLayer.addLayer(marker);
        if (key) seenEventKeys.add(key);
    });
    existingEventMarkers.forEach((marker, key) => {
        if (seenEventKeys.has(key) || (animator && animator.isRetiring(marker))) return;
        if (animator && direction === 'out') {
            const ll = marker.getLatLng();
            const target = findContainingCluster(addedClusters, ll.lat, ll.lng);
            if (target) {
                animator.retire(eventsLayer, marker, [target['center_lat'], target['center_lon']]);
                return;
            }
        }
        eventsLayer.removeLayer(marker);
    });
};

const syncClusterMarkersLayer = ({clustersLayer, mapClusters, direction, existingClusterMarkers, addedClusters, removedClusters, animator, createMarker, skipKey}) => {
    const seenClusterKeys = new Set();
    mapClusters.forEach(cl => {
        const key = getClusterKey(cl);
        if (skipKey && key === skipKey) {
            seenClusterKeys.add(key);
            return;
        }
        const reused = existingClusterMarkers.get(key);
        if (reused) {
            if (animator && animator.isRetiring(reused)) animator.resurrect(reused);
            reused['_cluster'] = cl;
            seenClusterKeys.add(key);
            return;
        }
        const marker = createMarker(cl);
        if (animator && direction === 'in') {
            const parent = findContainingCluster(removedClusters, cl['center_lat'], cl['center_lon']);
            if (parent) animator.spawnFromParent(marker, [parent['center_lat'], parent['center_lon']]);
        } else if (animator && direction === 'out') {
            animator.fadeIn(marker);
        }
        clustersLayer.addLayer(marker);
        seenClusterKeys.add(key);
    });
    existingClusterMarkers.forEach((marker, key) => {
        if (seenClusterKeys.has(key) || (animator && animator.isRetiring(marker))) return;
        if (animator && direction === 'in') {
            animator.retire(clustersLayer, marker);
            return;
        }
        if (animator && direction === 'out') {
            const cl = marker['_cluster'];
            const target = findContainingCluster(addedClusters, cl['center_lat'], cl['center_lon']);
            if (target) {
                animator.retire(clustersLayer, marker, [target['center_lat'], target['center_lon']]);
                return;
            }
        }
        clustersLayer.removeLayer(marker);
    });
};

const normalizeSlug = (value) => {
    if (!value || value === 'None' || value === 'null') return null;
    return value;
};

const getEventMarkerKey = (event) => {
    if (!event) return null;
    if (event['slug']) return event['slug'];
    if (event['detail_url'] && event['detail_url'] !== '#') return event['detail_url'];
    return `${event['name']}_${event['latitude']}_${event['longitude']}`;
};

const setPopupContentWithoutAutoPan = (marker, popup, content) => {
    const previousAutoPan = popup.options ? popup.options.autoPan : undefined;
    if (popup.options) popup.options.autoPan = false;
    marker.setPopupContent(content);
    if (popup.options) popup.options.autoPan = previousAutoPan;
};

const applyDateFilterFromInput = (dateValueString, changeDateFilter) => {
    if (!dateValueString) return;
    const selectedDateObj = new Date(dateValueString + 'T00:00:00');
    const timestamp = selectedDateObj.getTime();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    if (timestamp === today.getTime()) {
        changeDateFilter(null);
    } else if (timestamp === tomorrow.getTime()) {
        changeDateFilter(tomorrow.getTime());
    } else {
        changeDateFilter(timestamp);
    }
};

const HomeMap = ({initialEventsData, initialLocation, initialEventData, detailView = false, hasSSRPanel = false, citiesData = [], mapConfig = {}}) => {
    const mapConfigRef = useRef(mapConfig);
    mapConfigRef.current = mapConfig;
    const getMapLabels = useCallback(() => (mapConfigRef.current && mapConfigRef.current.labels) || {}, []);
    const LIST_BATCH_SIZE = (mapConfig && mapConfig.listBatchSize) || DEFAULT_LIST_BATCH_SIZE;
    const mapRef = useRef(null);
    const [map, setMap] = useState(/** @type {L.Map | null} */ (null));
    const [eventsLayer, setEventsLayer] = useState(/** @type {L.LayerGroup | null} */ (null));
    const [clustersLayer, setClustersLayer] = useState(/** @type {L.LayerGroup | null} */ (null));
    const detailSeedMarkers = (detailView && Array.isArray(initialEventsData))
        ? initialEventsData.filter(e => e && Number.isFinite(e.latitude) && Number.isFinite(e.longitude))
        : [];
    const [mapMarkers, setMapMarkers] = useState(detailSeedMarkers);
    const [mapClusters, setMapClusters] = useState([]);
    const [isDetailModeActive, setIsDetailModeActive] = useState(detailView);
    const isDetailModeActiveRef = useRef(isDetailModeActive);
    isDetailModeActiveRef.current = isDetailModeActive;
    const [allEventsData, setAllEventsData] = useState(initialEventsData || []);
    const [userLocation, setUserLocation] = useState(null);
    const [displayedEventsCount, setDisplayedEventsCount] = useState(0);
    const [currentSortedEvents, setCurrentSortedEvents] = useState([]);
    const [currentDateFilter, setCurrentDateFilter] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [showSkeletons, setShowSkeletons] = useState(false);
    const [showOngoingOnly, setShowOngoingOnly] = useState(false);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);
    const [isSubmitOpen, setIsSubmitOpen] = useState(false);
    const [selectedCity, setSelectedCity] = useState(() => /** @type {string} */ (localStorage.getItem('selectedCity') || ''));
    const [ipCityName, setIpCityName] = useState(/** @type {string} */ (''));
    const selectedCityRef = useRef(/** @type {string} */ (selectedCity));
    selectedCityRef.current = selectedCity;
    const prevShowOngoingOnlyRef = useRef(showOngoingOnly);
    const userLocationMarkerRef = useRef(null);
    const userLocationAreaRef = useRef(null);
    const isInitializedRef = useRef(false);
    const eventsDataFromFetchRef = useRef(false);
    const renderedListRef = useRef(null);
    const allEventsDataRef = useRef(allEventsData);
    const handleEventClickRef = useRef(null);
    const changeDateFilterRef = useRef(null);
    const currentDateFilterRef = useRef(currentDateFilter);
    const mapMarkersRef = useRef(mapMarkers);
    const mapClustersRef = useRef(mapClusters);
    const mapRequestAbortRef = useRef(null);
    const initialBoundsAbortRef = useRef(null);
    const spiderfyLayerRef = useRef(null);
    const spiderfyAbortRef = useRef(null);
    const activeSpiderfyRef = useRef(null);
    const zoomAnimatorRef = useRef(null);
    const mapDataZoomRef = useRef(null);
    const appliedDataZoomRef = useRef(null);
    const lastFetchSignatureRef = useRef(null);
    const pendingPopupSlugRef = useRef(null);
    const pendingPopupMatchRef = useRef(null);
    const standalonePopupRef = useRef(null);
    const popupRequestRef = useRef(0);
    const eventUrlCacheRef = useRef(new Map());
    const eventUrlPendingRef = useRef(new Map());
    const landingSlugRef = useRef(detailView && initialEventData ? normalizeSlug(initialEventData['slug']) : null);
    const pendingPopupRequestIdRef = useRef(0);
    const showOngoingOnlyRef = useRef(showOngoingOnly);
    allEventsDataRef.current = allEventsData;
    currentDateFilterRef.current = currentDateFilter;
    mapMarkersRef.current = mapMarkers;
    mapClustersRef.current = mapClusters;
    showOngoingOnlyRef.current = showOngoingOnly;
    const visibleEvents = useMemo(
        () => currentSortedEvents.slice(0, displayedEventsCount),
        [currentSortedEvents, displayedEventsCount]
    );

    const setupTileLayer = useCallback((leafletMap) => {
        const baseOpts = {
            maxZoom: 19,
            minZoom: 2,
            updateWhenIdle: false,
            updateWhenZooming: true,
            keepBuffer: 4,
            noWrap: true,
            bounds: L.latLngBounds(L.latLng(-85.05112878, -180), L.latLng(85.05112878, 180))
        };
        const dark = L.tileLayer(cartoTileUrl('dark_all'), {
            ...baseOpts,
            subdomains: 'abcd',
            maxZoom: 20,
            attribution: CARTO_ATTRIBUTION
        });
        const light = L.tileLayer(cartoTileUrl('light_all'), {
            ...baseOpts,
            subdomains: 'abcd',
            maxZoom: 20,
            attribution: CARTO_ATTRIBUTION
        });
        setupThemeSwitching(leafletMap, light, dark);
    }, []);

    const updateMinFitZoom = useCallback((leafletMap, worldBounds) => {
        if (!leafletMap) return;
        const fitZoom = leafletMap.getBoundsZoom(worldBounds, true);
        if (!Number.isFinite(fitZoom)) return;
        const safeMinZoom = Math.max(2, fitZoom);
        if (leafletMap.getMinZoom() !== safeMinZoom) {
            leafletMap.setMinZoom(safeMinZoom);
        }
        if (leafletMap.getZoom() < safeMinZoom) {
            leafletMap.setZoom(safeMinZoom);
        }
    }, []);

    const clampViewportToWorldBounds = useCallback((leafletMap, worldBounds) => {
        const clamp = () => {
            if (clamp._lock) return;
            const zoom = leafletMap.getZoom();
            const worldSWPx = leafletMap.project(worldBounds.getSouthWest(), zoom);
            const worldNEPx = leafletMap.project(worldBounds.getNorthEast(), zoom);
            const minX = Math.min(worldSWPx.x, worldNEPx.x);
            const maxX = Math.max(worldSWPx.x, worldNEPx.x);
            const minY = Math.min(worldSWPx.y, worldNEPx.y);
            const maxY = Math.max(worldSWPx.y, worldNEPx.y);
            const centerPx = leafletMap.project(leafletMap.getCenter(), zoom);
            const half = leafletMap.getSize().divideBy(2);
            const topY = centerPx.y - half.y;
            const bottomY = centerPx.y + half.y;
            const leftX = centerPx.x - half.x;
            const rightX = centerPx.x + half.x;
            let dx = 0;
            let dy = 0;
            if (topY < minY) dy = minY - topY;
            else if (bottomY > maxY) dy = maxY - bottomY;
            if (leftX < minX) dx = minX - leftX;
            else if (rightX > maxX) dx = maxX - rightX;
            if (dx === 0 && dy === 0) return;
            const newCenterPx = L.point(centerPx.x + dx, centerPx.y + dy);
            const newCenter = leafletMap.unproject(newCenterPx, zoom);
            clamp._lock = true;
            leafletMap.setView(newCenter, zoom, { animate: false });
            clamp._lock = false;
        };
        leafletMap.on('move zoomend resize', clamp);
        clamp();
    }, []);

    const renderUserLocationLayers = useCallback((targetMap, latitude, longitude, accuracyMeters) => {
        if (userLocationMarkerRef.current) {
            targetMap.removeLayer(userLocationMarkerRef.current);
            userLocationMarkerRef.current = null;
        }
        if (userLocationAreaRef.current) {
            targetMap.removeLayer(userLocationAreaRef.current);
            userLocationAreaRef.current = null;
        }
        if (!Number.isFinite(accuracyMeters)) {
            userLocationMarkerRef.current = createPreciseLocationMarker(latitude, longitude).addTo(targetMap);
            return;
        }
        const label = getMapLabels().approximateLocation;
        userLocationAreaRef.current = createApproximateLocationLayer(latitude, longitude, accuracyMeters, label)
            .addTo(targetMap);
    }, [getMapLabels]);

    useEffect(() => {
        if (!mapRef.current || map) return;
        const mapLang = getCurrentLanguage();
        const worldBounds = L.latLngBounds(L.latLng(-85.05112878, -180), L.latLng(85.05112878, 180));
        const mapOptions = {
            zoomControl: false,
            attributionControl: false,
            minZoom: 2,
            maxZoom: 19,
            maxBounds: worldBounds,
            maxBoundsViscosity: 1.0,
            worldCopyJump: false
        };
        if (initialLocation?.latitude && initialLocation?.longitude) {
            mapOptions.center = [initialLocation.latitude, initialLocation.longitude];
            mapOptions.zoom = initialLocation.zoom || 11;
            console.log('[homeMap] Initializing map with SSR location:', {
                latitude: initialLocation.latitude,
                longitude: initialLocation.longitude,
                zoom: mapOptions.zoom,
                matchType: initialLocation && 'match_type' in initialLocation ? initialLocation['match_type'] : null
            });
        } else {
            mapOptions.center = [55.75, 37.62];
            mapOptions.zoom = 10;
        }
        const leafletMap = L.map('map', mapOptions);
        leafletMap.options.closePopupOnClick = false;
        console.log('[homeMap] map created, mapLang=', mapLang);
        setupTileLayer(leafletMap);
        updateMinFitZoom(leafletMap, worldBounds);
        clampViewportToWorldBounds(leafletMap, worldBounds);
        const handleResize = () => updateMinFitZoom(leafletMap, worldBounds);
        window.addEventListener('resize', handleResize);
        leafletMap.on('remove', () => window.removeEventListener('resize', handleResize));
        const markersGroup = L.layerGroup();
        const clustersGroup = L.layerGroup();
        leafletMap.addLayer(markersGroup);
        leafletMap.addLayer(clustersGroup);
        setEventsLayer(markersGroup);
        setClustersLayer(clustersGroup);
        zoomAnimatorRef.current = new ClusterZoomAnimator(leafletMap);
        if (typeof markerClusterGroup === 'function') {
            const spiderfyGroup = markerClusterGroup({
                maxClusterRadius: 80,
                showCoverageOnHover: false,
                spiderfyOnMaxZoom: false,
                zoomToBoundsOnClick: false,
                chunkedLoading: true,
                iconCreateFunction: (cluster) => {
                    const count = cluster.getChildCount();
                    let sizeClass = 'small';
                    if (count > 100) sizeClass = 'large';
                    else if (count > 10) sizeClass = 'medium';
                    return L.divIcon({
                        html: `<div class="cluster-icon cluster-${sizeClass}">${count}</div>`,
                        className: 'custom-cluster',
                        iconSize: [40, 40]
                    });
                }
            });
            leafletMap.addLayer(spiderfyGroup);
            spiderfyLayerRef.current = spiderfyGroup;
        } else {
            console.warn('[homeMap] markerClusterGroup not available — spiderfy disabled');
        }
        const ssrBounds = initialLocation && initialLocation['bounds'];
        const latMin = ssrBounds && ssrBounds['lat_min'];
        const latMax = ssrBounds && ssrBounds['lat_max'];
        const lonMin = ssrBounds && ssrBounds['lon_min'];
        const lonMax = ssrBounds && ssrBounds['lon_max'];
        if (Number.isFinite(latMin) && Number.isFinite(latMax)
            && Number.isFinite(lonMin) && Number.isFinite(lonMax)) {
            const configMaxZoom = Number.isFinite(mapConfig && mapConfig.maxZoom) ? mapConfig.maxZoom : 14;
            leafletMap.fitBounds(
                [[latMin, lonMin], [latMax, lonMax]],
                {maxZoom: configMaxZoom, padding: [40, 40]}
            );
            console.log('[homeMap] Applied SSR bounds fitBounds:', ssrBounds, 'count=', ssrBounds['count']);
        }
        const ssrLocationSource = initialLocation && initialLocation['match_type'];
        if (isIpLocationSource(ssrLocationSource)
            && Number.isFinite(initialLocation.latitude) && Number.isFinite(initialLocation.longitude)) {
            setUserLocation([initialLocation.latitude, initialLocation.longitude]);
            renderUserLocationLayers(
                leafletMap,
                initialLocation.latitude,
                initialLocation.longitude,
                getIpLocationAccuracyMeters(ssrLocationSource)
            );
            calculateDistances(initialLocation.latitude, initialLocation.longitude);
        }
        setMap(leafletMap);
        const timeoutId = setTimeout(() => leafletMap.invalidateSize(), 150);
        return () => {
            clearTimeout(timeoutId);
            if (zoomAnimatorRef.current) {
                zoomAnimatorRef.current.finish();
                zoomAnimatorRef.current = null;
            }
            leafletMap.remove();
        };
    }, []);

    const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
        const toNumber = (value) => (typeof value === 'number' ? value : parseFloat(value));
        const latitude1 = toNumber(lat1);
        const longitude1 = toNumber(lon1);
        const latitude2 = toNumber(lat2);
        const longitude2 = toNumber(lon2);
        if (![latitude1, longitude1, latitude2, longitude2].every(Number.isFinite)) {
            return undefined;
        }
        const R = 6371;
        const dLat = (latitude2 - latitude1) * Math.PI / 180;
        const dLon = (longitude2 - longitude1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(latitude1 * Math.PI / 180) * Math.cos(latitude2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }, []);

    const calculateDistances = useCallback((lat, lng, eventsList = null) => {
        const mapEventsWithDistance = (events) => {
            if (!Array.isArray(events)) {
                return events;
            }
            return events.map(event => {
                if (!event || event.latitude === undefined || event.longitude === undefined) {
                    return event;
                }
                const distance = calculateDistance(lat, lng, event.latitude, event.longitude);
                return {
                    ...event,
                    distance: typeof distance === 'number' ? distance : event.distance,
                };
            });
        };
        if (eventsList !== null) {
            return mapEventsWithDistance(eventsList);
        }
        setAllEventsData(prevEvents => mapEventsWithDistance(prevEvents));
        return null;
    }, [calculateDistance]);

    const updateUserLocationOnMap = useCallback((lat, lng, options = {}) => {
        const latitude = typeof lat === 'number' ? lat : parseFloat(lat);
        const longitude = typeof lng === 'number' ? lng : parseFloat(lng);
        if (![latitude, longitude].every(Number.isFinite)) {
            return;
        }
        setUserLocation([latitude, longitude]);
        if (!map) {
            return;
        }
        const desiredZoom = options.zoomLevel !== undefined ? options.zoomLevel : (map.getZoom() < 12 ? 12 : map.getZoom());
        if (options.shouldCenter !== false) {
            map.setView([latitude, longitude], desiredZoom);
        }
        renderUserLocationLayers(map, latitude, longitude, options.accuracyMeters);
        if (options.recalculateDistances !== false) {
            calculateDistances(latitude, longitude);
        }
    }, [map, calculateDistances, renderUserLocationLayers]);

    useEffect(() => {
        if (!map) {
            return;
        }
        let isCancelled = false;
        const fetchLocationByIp = async (forceUpdate = false) => {
            try {
                const forceParam = forceUpdate ? '?force_update=true' : '';
                const response = await fetch(`/api/location/${forceParam}`);
                if (!response.ok) {
                    if (!isCancelled) {
                        console.error('Failed to resolve location by IP:', `HTTP error! status: ${response.status}`);
                    }
                    return;
                }
                const data = await response.json();
                if (isCancelled) {
                    return;
                }
                const location = data?.location;
                if (!location) {
                    return;
                }
                const latitude = location.latitude;
                const longitude = location.longitude;
                const matchType = location && 'match_type' in location ? location['match_type'] : 'unknown';
                const needsUpdate = location && 'needs_update' in location ? location['needs_update'] : false;
                if (latitude !== undefined && longitude !== undefined) {
                    console.log('[homeMap] IP-based location resolved:', {
                        city: location.city,
                        region: location.region,
                        country: location.country,
                        latitude,
                        longitude,
                        method: location.method,
                        matchType: matchType,
                        needsUpdate: needsUpdate,
                        matchedIp: location && 'matched_ip' in location ? location['matched_ip'] : null,
                        raw: data,
                    });
                    setIpCityName(location.city || '');
                    if (!localStorage.getItem('selectedCity') && citiesData.length > 0) {
                        const detectedCity = citiesData.find(c =>
                            latitude >= c['lat_min'] && latitude <= c['lat_max'] &&
                            longitude >= c['lon_min'] && longitude <= c['lon_max']
                        );
                        if (detectedCity) {
                            setSelectedCity(detectedCity['key']);
                            localStorage.setItem('selectedCity', detectedCity['key']);
                        }
                    }
                    const isSameAsSSR = initialLocation &&
                        Math.abs(latitude - initialLocation.latitude) < 0.0001 &&
                        Math.abs(longitude - initialLocation.longitude) < 0.0001;
                    const isIpBasedLocation = isIpLocationSource(location.method);
                    const shouldCenter = !forceUpdate && !isSameAsSSR;
                    console.log('[homeMap] Update map decision:', {
                        shouldCenter,
                        isSameAsSSR,
                        forceUpdate,
                        ssrLat: initialLocation?.latitude,
                        ssrLng: initialLocation?.longitude,
                        newLat: latitude,
                        newLng: longitude
                    });
                    updateUserLocationOnMap(latitude, longitude, {
                        zoomLevel: 12,
                        recalculateDistances: true,
                        accuracyMeters: isIpBasedLocation ? getIpLocationAccuracyMeters(location.method) : undefined,
                        shouldCenter: shouldCenter
                    });
                    if (needsUpdate && !forceUpdate) {
                        console.log('[homeMap] Location match is not exact, requesting precise location in background...');
                        setTimeout(() => {
                            if (!isCancelled) {
                                fetchLocationByIp(true);
                            }
                        }, 2000);
                    }
                }
            } catch (error) {
                if (!isCancelled) {
                    console.error('Failed to resolve location by IP:', error);
                }
            }
        };
        fetchLocationByIp(false).catch(error => {
            if (!isCancelled) {
                console.error('Failed to resolve location:', error);
            }
        });
        return () => {
            isCancelled = true;
        };
    }, [map, updateUserLocationOnMap]);

    const extractDomain = useCallback((url) => {
        try {
            const urlObj = new URL(url);
            return urlObj.hostname.replace(/^www\./, '');
        } catch (e) {
            return null;
        }
    }, []);
    const getFaviconUrl = useCallback((domain) => {
        if (!domain) return null;
        return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=16`;
    }, []);
    const disableLeafletPopupAutoToggle = useCallback((marker) => {
        if (!marker || !marker.off) {
            return;
        }
        const openPopupHandler = marker['_openPopup'];
        if (openPopupHandler) {
            marker.off('click', openPopupHandler, marker);
            marker.off('keypress', openPopupHandler, marker);
        }
    }, []);
    const closeAllPopups = useCallback((keptPopup) => {
        if (!map) {
            return;
        }
        const openPopups = [];
        map.eachLayer(layer => {
            if (layer instanceof L.Popup && layer !== keptPopup) {
                openPopups.push(layer);
            }
        });
        openPopups.forEach(popup => map.closePopup(popup));
        if (standalonePopupRef.current && standalonePopupRef.current !== keptPopup) {
            standalonePopupRef.current = null;
        }
    }, [map]);
    const beginPopupRequest = useCallback(() => {
        popupRequestRef.current += 1;
        return popupRequestRef.current;
    }, []);
    const isCurrentPopupRequest = useCallback((requestId) => {
        return requestId === undefined || requestId === popupRequestRef.current;
    }, []);
    const forceOpenMarkerPopup = useCallback((marker, requestId) => {
        if (!map || !marker || !isCurrentPopupRequest(requestId)) {
            return false;
        }
        const popup = marker.getPopup ? marker.getPopup() : null;
        if (!popup) {
            return false;
        }
        if (popup.options) {
            popup.options.closeOnClick = false;
            popup.options.autoClose = false;
        }
        if (popup.setLatLng && marker.getLatLng) {
            popup.setLatLng(marker.getLatLng());
        }
        popup._source = marker;
        closeAllPopups(popup);
        map._popup = popup;
        if (!map.hasLayer(popup)) {
            map.addLayer(popup);
        }
        return true;
    }, [map, closeAllPopups, isCurrentPopupRequest]);
    const getSlugFromEvent = useCallback((event) => {
        const slug = event && normalizeSlug(event['slug']);
        if (slug) return slug;
        const detailUrl = event && event['detail_url'];
        if (!detailUrl || detailUrl === '#') return null;
        const path = detailUrl.replace(/\/$/, '').split('/');
        return path.length ? path[path.length - 1] : null;
    }, []);

    /**
     * Loads the card of an event. The first request for the event of the detail page the
     * visitor landed on is tagged via=landing (the page opens it by itself), every other
     * request via=click, so the hit log can tell a landing from an opened card.
     */
    const fetchEventDetails = useCallback((event) => {
        const slug = getSlugFromEvent(event);
        if (!slug) return Promise.resolve(null);
        const lang = getCurrentLanguage();
        const via = slug === landingSlugRef.current ? 'landing' : 'click';
        if (via === 'landing') landingSlugRef.current = null;
        const apiUrl = `/api/events/${encodeURIComponent(slug)}/?language=${lang}&via=${via}`;
        return fetch(apiUrl)
            .then(res => res.ok ? res.json() : null)
            .then(data => data && data.event ? data.event : null)
            .catch(() => null);
    }, [getSlugFromEvent]);

    const applyEventUrl = useCallback((event, loaded) => {
        event['url'] = loaded['url'];
        if (loaded['sources']) event['sources'] = loaded['sources'];
        if (loaded['translated_sources']) event['translated_sources'] = loaded['translated_sources'];
        if (loaded['outbound_links']) event['outbound_links'] = loaded['outbound_links'];
    }, []);

    /**
     * Fills the source link of an event from its card, once per slug: a popup that opens
     * again while the card is still loading (the detail page re-runs its auto-open on every
     * map response) waits for the request in flight instead of sending a second one.
     */
    const ensureEventUrl = useCallback((event, onLoaded) => {
        const slug = getSlugFromEvent(event);
        const cached = slug ? eventUrlCacheRef.current.get(slug) : undefined;
        if (cached !== undefined) {
            applyEventUrl(event, cached);
            return false;
        }
        if (!slug) return false;
        let pending = eventUrlPendingRef.current.get(slug);
        if (!pending) {
            pending = fetchEventDetails(event).then(details => {
                eventUrlPendingRef.current.delete(slug);
                if (details) {
                    eventUrlCacheRef.current.set(slug, {
                        url: details['url'] || null,
                        sources: details['sources'],
                        translated_sources: details['translated_sources'],
                        outbound_links: details['outbound_links'],
                    });
                }
            });
            eventUrlPendingRef.current.set(slug, pending);
        }
        pending.then(() => {
            const loaded = eventUrlCacheRef.current.get(slug);
            if (loaded) applyEventUrl(event, loaded);
            onLoaded();
        }).catch(onLoaded);
        return true;
    }, [getSlugFromEvent, fetchEventDetails, applyEventUrl]);

    const getTimeFromSSRList = useCallback((event) => {
        if (!event || !event.detail_url) return null;
        const known = (allEventsDataRef.current || []).find(item => item.detail_url === event.detail_url);
        if (known && known.display_time) return known.display_time;
        const listEl = document.getElementById('ssr-results-list');
        if (!listEl) return null;
        const items = listEl.querySelectorAll('a.result-item');
        const link = Array.from(items).find(el => el.getAttribute('href') === event.detail_url);
        if (!link) return null;
        const wrap = link.querySelector('.result-time-text');
        return wrap ? wrap.textContent.trim() : null;
    }, []);

    const buildPopupContent = useCallback((event, isLoadingUrl = false) => {
        const lang = getCurrentLanguage();
        const t = getMapLabels();
        const parts = formatEventListTimeParts(event, lang);
        let timePrimary = parts.primary;
        let timeVenue = parts.venue;
        if (!timePrimary || isTimeUnknown(timePrimary)) {
            const ssrTime = getTimeFromSSRList(event);
            if (ssrTime) timePrimary = ssrTime;
            timeVenue = null;
        }
        const timeHtml = timeVenue
            ? `${escapeHtml(timePrimary)}<br><span class="popup-time-venue">${escapeHtml(timeVenue)}</span>`
            : escapeHtml(timePrimary);
        const translatedSources = event.translated_sources || {};
        const buildTranslateLinks = (url) => {
            const links = Array.isArray(translatedSources[url]) ? translatedSources[url] : [];
            if (!links.length) return '';
            const anchors = links.map(link => `<a href="${escapeHtml(link.url)}" target="_blank" rel="noopener" class="popup-translate-link" data-provider="${escapeHtml(link.provider)}">${escapeHtml(link.label)}</a>`);
            return `<div class="popup-translate">
                <i class="fas fa-language" aria-hidden="true"></i>
                <span class="popup-translate-title">${escapeHtml(t.translate || '')}:</span>
                ${anchors.join('<span class="popup-translate-separator" aria-hidden="true">·</span>')}
            </div>`;
        };
        const outboundLinks = event.outbound_links || {};
        const buildAdLabel = (outbound) => {
            const parts = [t.advertisement, outbound.advertiser, outbound.erid ? `erid: ${outbound.erid}` : ''].filter(Boolean);
            return `<div class="popup-ad-label">${parts.map(escapeHtml).join(' · ')}</div>`;
        };
        const buildSourceLink = (url) => {
            const outbound = outboundLinks[url];
            const href = escapeHtml(outbound ? outbound.url : url);
            const rel = outbound ? 'sponsored noopener' : 'noopener';
            const afterLink = `${outbound ? buildAdLabel(outbound) : ''}${buildTranslateLinks(url)}`;
            const domain = extractDomain(url);
            if (!domain) {
                return `<a href="${href}" target="_blank" rel="${rel}" class="popup-link">${t.more} <i class="fas fa-external-link-alt"></i></a>${afterLink}`;
            }
            const faviconUrl = getFaviconUrl(domain);
            return `<a href="${href}" target="_blank" rel="${rel}" class="popup-link">
                ${faviconUrl ? `<img src="${faviconUrl}" alt="" class="popup-link-favicon" />` : ''}
                <span class="popup-link-domain">${domain}</span>
                <i class="fas fa-external-link-alt"></i>
            </a>${afterLink}`;
        };
        const sources = Array.isArray(event.sources) ? event.sources : [];
        let linkHtml = '';
        if (sources.length > 1) {
            linkHtml = `<div class="popup-sources">
                <span class="popup-sources-title">${escapeHtml(t.sources || '')}</span>
                ${sources.map(buildSourceLink).join('')}
            </div>`;
        } else if (event.url) {
            linkHtml = buildSourceLink(event.url);
        } else if (isLoadingUrl) {
            linkHtml = `<div class="popup-link-skeleton" aria-hidden="true">
                <span class="popup-link-skeleton__favicon"></span>
                <span class="popup-link-skeleton__domain"></span>
                <span class="popup-link-skeleton__icon"></span>
            </div>`;
        }
        return `
          <div class="popup-content">
            <h4>${event.name}</h4>
            ${timePrimary ? `<p class="popup-event-time"><i class="fas fa-clock"></i><span class="popup-time-stack">${timeHtml}</span></p>` : ''}
            ${event.distance !== undefined ? `<p class="popup-distance"><i class="fas fa-route"></i> ${event.distance.toFixed(1)} ${t.km}</p>` : ''}
            ${linkHtml}
            <p class="popup-disclaimer"><i class="fas fa-info-circle"></i> ${t.disclaimer}</p>
          </div>
        `;
    }, [extractDomain, getFaviconUrl, getTimeFromSSRList, getMapLabels]);

    const ensurePopupForMarker = useCallback((marker, event) => {
        const render = (isLoadingUrl) => {
            const existingPopup = marker.getPopup();
            const popupContent = buildPopupContent(event, isLoadingUrl);
            if (!existingPopup) {
                marker.bindPopup(popupContent, {
                    maxWidth: 250,
                    className: 'custom-popup',
                    autoPan: true,
                    autoPanPadding: [50, 50],
                    closeOnClick: false,
                    autoClose: false
                });
                disableLeafletPopupAutoToggle(marker);
                return;
            }
            if (existingPopup.options) {
                existingPopup.options.closeOnClick = false;
                existingPopup.options.autoClose = false;
            }
            setPopupContentWithoutAutoPan(marker, existingPopup, popupContent);
            disableLeafletPopupAutoToggle(marker);
        };
        const isLoadingUrl = ensureEventUrl(event, () => render(false));
        render(isLoadingUrl);
    }, [buildPopupContent, disableLeafletPopupAutoToggle, ensureEventUrl]);

    const openStandalonePopupForEvent = useCallback((event, latlng, requestId) => {
        if (!map || !isCurrentPopupRequest(requestId)) return;
        const popup = L.popup({
            maxWidth: 250,
            className: 'custom-popup',
            autoPan: true,
            autoPanPadding: [50, 50],
            closeOnClick: false,
            autoClose: false
        });
        const refresh = () => {
            if (standalonePopupRef.current === popup && map.hasLayer(popup)) {
                popup.setContent(buildPopupContent(event, false));
            }
        };
        const isLoadingUrl = ensureEventUrl(event, refresh);
        popup.setLatLng(latlng).setContent(buildPopupContent(event, isLoadingUrl));
        closeAllPopups();
        standalonePopupRef.current = popup;
        map.openPopup(popup);
    }, [map, buildPopupContent, ensureEventUrl, closeAllPopups, isCurrentPopupRequest]);

    const getDetailUrl = useCallback((event) => {
        if (event && event['detail_url']) return event['detail_url'];
        const slug = event && normalizeSlug(event['slug']);
        if (!slug) return null;
        const lang = getCurrentLanguage();
        const pathSegment = lang === LANG_RU ? 'sobytiya' : 'events';
        return `/${lang}/${pathSegment}/${slug}/`;
    }, []);

    const updateUrlForEvent = useCallback((event, replace = false) => {
        const url = getDetailUrl(event);
        if (url) {
            const method = replace ? 'replaceState' : 'pushState';
            window.history[method]({}, '', url);
        }
    }, [getDetailUrl]);

    const createEventMarker = useCallback((event) => {
        const isOngoing = event && 'is_ongoing' in event ? event['is_ongoing'] : false;
        const icon = L.divIcon({
            className: 'custom-marker',
            html: `<div class="marker-pin ${isOngoing ? 'ongoing' : ''}"></div>`,
            iconSize: [30, 42],
            iconAnchor: [15, 42],
            popupAnchor: [0, -42]
        });
        const markerLabels = getMapLabels();
        const markerName = event && event['name'] ? event['name'] : markerLabels.eventMarker;
        const marker = L.marker([event.latitude, event.longitude], {
            icon: icon,
            riseOnHover: true,
            bubblingMouseEvents: false,
            title: markerName,
            alt: markerName
        });
        event._marker = marker;
        marker['_eventKey'] = getEventMarkerKey(event);
        marker['_eventData'] = event;
        marker.on('click', function (e) {
            if (e && e.originalEvent && L && L.DomEvent) {
                L.DomEvent.stop(e.originalEvent);
            }
            const currentEvent = this['_eventData'] || event;
            const url = getDetailUrl(currentEvent);
            if (url) {
                window.history.pushState({}, '', url);
            }
            const requestId = beginPopupRequest();
            const showPopup = () => {
                if (!isCurrentPopupRequest(requestId)) {
                    return;
                }
                ensurePopupForMarker(this, currentEvent);
                const popup = this.getPopup();
                if (popup) {
                    forceOpenMarkerPopup(this, requestId);
                }
            };
            showPopup();
        });
        return marker;
    }, [ensurePopupForMarker, map, forceOpenMarkerPopup, getDetailUrl, beginPopupRequest, isCurrentPopupRequest]);

    const resetSpiderfy = useCallback(() => {
        if (spiderfyAbortRef.current) {
            try { spiderfyAbortRef.current.abort(); } catch (_) {}
            spiderfyAbortRef.current = null;
        }
        if (spiderfyLayerRef.current) {
            spiderfyLayerRef.current.clearLayers();
        }
        activeSpiderfyRef.current = null;
    }, []);

    useEffect(() => {
        if (!map) return;
        const onZoomStart = () => {
            resetSpiderfy();
            if (zoomAnimatorRef.current) zoomAnimatorRef.current.finish();
        };
        const onViewReset = () => {
            if (zoomAnimatorRef.current) zoomAnimatorRef.current.finish();
        };
        map.on('zoomstart', onZoomStart);
        map.on('viewreset', onViewReset);
        return () => {
            map.off('zoomstart', onZoomStart);
            map.off('viewreset', onViewReset);
        };
    }, [map, resetSpiderfy]);

    const showSpiderfyForCluster = useCallback((cluster, originalMarker) => {
        if (!map || !spiderfyLayerRef.current) return;
        resetSpiderfy();
        if (clustersLayer && originalMarker) {
            clustersLayer.removeLayer(originalMarker);
        }
        activeSpiderfyRef.current = {marker: originalMarker, cluster};
        const params = new URLSearchParams();
        params.set('lat_min', String(cluster['lat_min']));
        params.set('lat_max', String(cluster['lat_max']));
        params.set('lon_min', String(cluster['lon_min']));
        params.set('lon_max', String(cluster['lon_max']));
        const tzOffset = new Date().getTimezoneOffset() * -1;
        params.set('timezone_offset', String(tzOffset));
        const ts = currentDateFilterRef.current;
        if (ts) params.set('timestamp', String(ts));
        if (showOngoingOnlyRef.current) params.set('is_now', 'true');
        if (selectedCityRef.current) params.set('city', selectedCityRef.current);
        params.set('language', getCurrentLanguage());
        const controller = new AbortController();
        spiderfyAbortRef.current = controller;
        fetch(`/api/events/cluster-markers/?${params.toString()}`, {signal: controller.signal})
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                console.log('[homeMap] showSpiderfyForCluster: fetch response', {markersCount: data && Array.isArray(data.markers) ? data.markers.length : -1});
                if (!data || !Array.isArray(data.markers) || data.markers.length === 0) return;
                const layer = spiderfyLayerRef.current;
                if (!layer) return;
                const leafletMarkers = data.markers.map(createEventMarker);
                layer.addLayers(leafletMarkers);
                const first = leafletMarkers[0];
                const parent = layer.getVisibleParent(first);
                if (parent && typeof parent.spiderfy === 'function') {
                    parent.spiderfy();
                }
                const pendingSlug = pendingPopupSlugRef.current;
                const pendingMatch = pendingPopupMatchRef.current;
                console.log('[homeMap] showSpiderfyForCluster: checking pending', {pendingSlug, hasMatcher: !!pendingMatch, availableSlugs: data.markers.map(m => m['slug'])});
                if (pendingSlug || pendingMatch) {
                    const targetEvent = data.markers.find(ev => {
                        if (pendingSlug && (ev['slug'] === pendingSlug ||
                            (ev['detail_url'] && ev['detail_url'].endsWith('/' + pendingSlug + '/')))) return true;
                        return pendingMatch ? pendingMatch(ev) : false;
                    });
                    const targetMarker = targetEvent && targetEvent['_marker'];
                    console.log('[homeMap] showSpiderfyForCluster: target match', {foundEvent: !!targetEvent, hasMarker: !!targetMarker});
                    if (targetMarker) {
                        const requestId = pendingPopupRequestIdRef.current;
                        pendingPopupSlugRef.current = null;
                        pendingPopupMatchRef.current = null;
                        ensurePopupForMarker(targetMarker, targetEvent);
                        setTimeout(() => {
                            console.log('[homeMap] showSpiderfyForCluster: opening popup now');
                            forceOpenMarkerPopup(targetMarker, requestId);
                        }, 400);
                    }
                }
            })
            .catch(err => {
                if (err && err.name !== 'AbortError') {
                    console.error('cluster-markers fetch failed:', err);
                }
            });
    }, [map, clustersLayer, createEventMarker, resetSpiderfy, ensurePopupForMarker, forceOpenMarkerPopup]);

    const activateCluster = useCallback((cluster, originalMarker) => {
        if (!map) {
            console.log('[homeMap] activateCluster: no map');
            return;
        }
        const latMin = cluster['lat_min'];
        const latMax = cluster['lat_max'];
        const lonMin = cluster['lon_min'];
        const lonMax = cluster['lon_max'];
        const centerLat = cluster['center_lat'];
        const latSpan = Math.abs(latMax - latMin);
        const lonSpan = Math.abs(lonMax - lonMin);
        const latMeters = latSpan * 111000;
        const lonMeters = lonSpan * 111000 * Math.cos((centerLat * Math.PI) / 180);
        const diagMeters = Math.sqrt(latMeters * latMeters + lonMeters * lonMeters);
        const DENSE_DIAG_METERS = 30;
        const currentZoom = map.getZoom() || 0;
        console.log('[homeMap] activateCluster', {cnt: cluster['cnt'], diagMeters: Math.round(diagMeters), currentZoom, hasOriginalMarker: !!originalMarker, hasSpiderfyLayer: !!spiderfyLayerRef.current});
        if ((diagMeters < DENSE_DIAG_METERS || currentZoom >= 18) && spiderfyLayerRef.current) {
            console.log('[homeMap] activateCluster -> spiderfy');
            showSpiderfyForCluster(cluster, originalMarker);
            return;
        }
        const bounds = L.latLngBounds(
            [latMin, lonMin],
            [latMax, lonMax]
        );
        const maxZoom = Math.min(currentZoom + 3, 19);
        console.log('[homeMap] activateCluster -> flyToBounds', {maxZoom, bounds: [[latMin, lonMin], [latMax, lonMax]]});
        map.flyToBounds(bounds, {maxZoom, padding: [40, 40], duration: 0.6});
    }, [map, showSpiderfyForCluster]);

    const createClusterMarker = useCallback((cluster) => {
        const count = cluster['cnt'];
        const centerLat = cluster['center_lat'];
        const centerLon = cluster['center_lon'];
        let sizeClass = 'small';
        if (count > 100) sizeClass = 'large';
        else if (count > 10) sizeClass = 'medium';
        const debugAttrs = [
            `data-cnt="${count}"`,
            `data-lat-min="${cluster['lat_min']}"`,
            `data-lat-max="${cluster['lat_max']}"`,
            `data-lon-min="${cluster['lon_min']}"`,
            `data-lon-max="${cluster['lon_max']}"`,
            `data-center-lat="${centerLat}"`,
            `data-center-lon="${centerLon}"`,
        ].join(' ');
        const icon = L.divIcon({
            html: `<div class="cluster-icon cluster-${sizeClass}" ${debugAttrs}>${count}</div>`,
            className: 'custom-cluster',
            iconSize: [40, 40]
        });
        const clusterLabel = `${getMapLabels().clusterGroup}: ${count}`;
        const marker = L.marker([centerLat, centerLon], {icon, riseOnHover: true, title: clusterLabel, alt: clusterLabel});
        marker['_cluster'] = cluster;
        marker.on('click', () => activateCluster(cluster, marker));
        return marker;
    }, [activateCluster]);

    const buildMapApiUrl = useCallback((bounds, zoom, timestamp, ongoingOnly, city) => {
        const params = new URLSearchParams();
        const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
        let latMin = clamp(bounds.getSouth(), -90, 90);
        let latMax = clamp(bounds.getNorth(), -90, 90);
        let lonMin = clamp(bounds.getWest(), -180, 180);
        let lonMax = clamp(bounds.getEast(), -180, 180);
        if (latMin >= latMax) { latMin = -90; latMax = 90; }
        if (lonMin >= lonMax) { lonMin = -180; lonMax = 180; }
        params.set('lat_min', latMin.toFixed(6));
        params.set('lat_max', latMax.toFixed(6));
        params.set('lon_min', lonMin.toFixed(6));
        params.set('lon_max', lonMax.toFixed(6));
        params.set('zoom', String(zoom));
        const tzOffset = new Date().getTimezoneOffset() * -1;
        params.set('timezone_offset', String(tzOffset));
        if (timestamp) params.set('timestamp', String(timestamp));
        if (ongoingOnly) params.set('is_now', 'true');
        const cityKey = city !== null && city !== undefined ? city : selectedCityRef.current;
        if (cityKey) params.set('city', cityKey);
        params.set('language', getCurrentLanguage());
        return `/api/events/map/?${params.toString()}`;
    }, []);

    const applyInitialBoundsForLocation = useCallback((lat, lng, cityKey, options = {}) => {
        if (!map) return;
        const cfg = mapConfigRef.current || {};
        const maxZoom = Number.isFinite(cfg.maxZoom) ? cfg.maxZoom : 14;
        const duration = options.duration || 1.2;
        const fallbackZoom = options.fallbackZoom || 11;
        const fallback = () => {
            map.flyTo([lat, lng], fallbackZoom, {duration});
        };
        if (initialBoundsAbortRef.current) {
            try { initialBoundsAbortRef.current.abort(); } catch (_) {}
        }
        const controller = new AbortController();
        initialBoundsAbortRef.current = controller;
        const params = new URLSearchParams();
        params.set('lat', String(lat));
        params.set('lng', String(lng));
        if (cityKey) params.set('city', cityKey);
        const ts = currentDateFilterRef.current;
        if (ts) params.set('timestamp', String(ts));
        const tzOffset = new Date().getTimezoneOffset() * -1;
        params.set('timezone_offset', String(tzOffset));
        if (showOngoingOnlyRef.current) params.set('is_now', 'true');
        params.set('language', getCurrentLanguage());
        fetch(`/api/events/initial-bounds/?${params.toString()}`, {signal: controller.signal})
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                const b = data && data['bounds'];
                const latMin = b && b['lat_min'];
                const latMax = b && b['lat_max'];
                const lonMin = b && b['lon_min'];
                const lonMax = b && b['lon_max'];
                if (Number.isFinite(latMin) && Number.isFinite(latMax)
                    && Number.isFinite(lonMin) && Number.isFinite(lonMax)) {
                    map.flyToBounds(
                        [[latMin, lonMin], [latMax, lonMax]],
                        {maxZoom, padding: [40, 40], duration}
                    );
                } else {
                    fallback();
                }
            })
            .catch(err => {
                if (err && err.name !== 'AbortError') {
                    console.error('initial-bounds fetch failed:', err);
                    fallback();
                }
            });
    }, [map]);

    const loadMapDataForViewport = useCallback(() => {
        if (!map) return;
        if (isDetailModeActiveRef.current) return;
        lastFetchSignatureRef.current = getViewSignature(map);
        const bounds = map.getBounds().pad(MAP_VIEWPORT_PAD);
        const zoom = Math.round(map.getZoom());
        if (mapRequestAbortRef.current) {
            try { mapRequestAbortRef.current.abort(); } catch (_) {}
        }
        const controller = new AbortController();
        mapRequestAbortRef.current = controller;
        const url = buildMapApiUrl(
            bounds, zoom,
            currentDateFilterRef.current,
            showOngoingOnlyRef.current,
            selectedCityRef.current
        );
        fetch(url, {signal: controller.signal})
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                if (!data) {
                    lastFetchSignatureRef.current = null;
                    return;
                }
                mapDataZoomRef.current = Number.isFinite(data['zoom']) ? data['zoom'] : zoom;
                setMapMarkers(Array.isArray(data['markers']) ? data['markers'] : []);
                setMapClusters(Array.isArray(data['clusters']) ? data['clusters'] : []);
            })
            .catch(err => {
                if (err && err.name !== 'AbortError') {
                    lastFetchSignatureRef.current = null;
                    console.error('Map data fetch failed:', err);
                }
            });
    }, [map, buildMapApiUrl]);

    useEffect(() => {
        if (!map) return;
        let timer = null;
        let dueAt = 0;
        const schedule = (delayMs) => {
            dueAt = Math.max(timer ? dueAt : 0, Date.now() + delayMs);
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                timer = null;
                if (getViewSignature(map) === lastFetchSignatureRef.current) return;
                loadMapDataForViewport();
            }, dueAt - Date.now());
        };
        const onMoveEnd = () => schedule(MAP_FETCH_DEBOUNCE_MS);
        const onZoomEnd = () => schedule(MAP_ZOOM_FETCH_DEBOUNCE_MS);
        map.on('moveend', onMoveEnd);
        map.on('zoomend', onZoomEnd);
        onMoveEnd();
        return () => {
            if (timer) clearTimeout(timer);
            map.off('moveend', onMoveEnd);
            map.off('zoomend', onZoomEnd);
        };
    }, [map, loadMapDataForViewport]);

    useEffect(() => {
        if (!map || !eventsLayer || !clustersLayer) return;
        const animator = zoomAnimatorRef.current;
        const dataZoom = mapDataZoomRef.current;
        const previousZoom = appliedDataZoomRef.current;
        appliedDataZoomRef.current = dataZoom;
        let direction = null;
        if (previousZoom !== null && dataZoom !== null && dataZoom > previousZoom) direction = 'in';
        if (previousZoom !== null && dataZoom !== null && dataZoom < previousZoom) direction = 'out';
        const {existingClusterMarkers, addedClusters, removedClusters} = collectClusterLayerDiff(clustersLayer, mapClusters);
        syncEventMarkersLayer({
            eventsLayer,
            mapMarkers,
            direction,
            removedClusters,
            addedClusters,
            animator,
            createMarker: createEventMarker,
            refreshPopup: (marker, ev) => {
                if (marker.getPopup()) ensurePopupForMarker(marker, ev);
            },
        });
        const activeSpiderfy = activeSpiderfyRef.current;
        const activeSpiderfyKey = activeSpiderfy && activeSpiderfy.cluster ? getClusterKey(activeSpiderfy.cluster) : null;
        const spiderfyStillValid = activeSpiderfyKey !== null && mapClusters.some(cl => getClusterKey(cl) === activeSpiderfyKey);
        syncClusterMarkersLayer({
            clustersLayer,
            mapClusters,
            direction,
            existingClusterMarkers,
            addedClusters,
            removedClusters,
            animator,
            createMarker: createClusterMarker,
            skipKey: spiderfyStillValid ? activeSpiderfyKey : null,
        });
        if (animator) animator.play();
        if (!spiderfyStillValid) {
            if (spiderfyLayerRef.current) {
                spiderfyLayerRef.current.clearLayers();
            }
            activeSpiderfyRef.current = null;
        }
        const pendingSlug = pendingPopupSlugRef.current;
        const pendingMatch = pendingPopupMatchRef.current;
        if (pendingSlug || pendingMatch) {
            const ev = mapMarkers.find(e => {
                if (pendingSlug && (e['slug'] === pendingSlug ||
                    (e['detail_url'] && e['detail_url'].endsWith('/' + pendingSlug + '/')))) return true;
                return pendingMatch ? pendingMatch(e) : false;
            });
            const evMarker = ev && ev['_marker'];
            console.log('[homeMap] viewport effect: pending', {
                pendingSlug,
                hasMatcher: !!pendingMatch,
                foundInMarkers: !!ev,
                hasMarker: !!evMarker,
                mapMarkersCount: mapMarkers.length,
                mapClustersCount: mapClusters.length,
                clusterBounds: mapClusters.map(c => ({lat_min: c['lat_min'], lat_max: c['lat_max'], lon_min: c['lon_min'], lon_max: c['lon_max'], cnt: c['cnt']})),
                markerCoords: mapMarkers.slice(0, 5).map(m => ({slug: m['slug'], lat: m['latitude'], lon: m['longitude']})),
            });
            if (evMarker) {
                const requestId = pendingPopupRequestIdRef.current;
                pendingPopupSlugRef.current = null;
                pendingPopupMatchRef.current = null;
                if (!(evMarker.isPopupOpen && evMarker.isPopupOpen())) {
                    ensurePopupForMarker(evMarker, ev);
                    setTimeout(() => {
                        console.log('[homeMap] viewport effect: opening popup now');
                        forceOpenMarkerPopup(evMarker, requestId);
                    }, 200);
                }
            } else {
                const allEvents = allEventsDataRef.current || [];
                const pendingEvent = allEvents.find(e => {
                    if (pendingSlug && (e['slug'] === pendingSlug ||
                        (e['detail_url'] && e['detail_url'].endsWith('/' + pendingSlug + '/')))) return true;
                    return pendingMatch ? pendingMatch(e) : false;
                });
                const pLat = pendingEvent && pendingEvent['latitude'];
                const pLon = pendingEvent && pendingEvent['longitude'];
                const containingCluster = (pLat != null && pLon != null)
                    ? mapClusters.find(cl => bboxContains(cl, pLat, pLon))
                    : null;
                console.log('[homeMap] viewport effect: recursive cluster search', {pendingEvent: !!pendingEvent, pLat, pLon, foundCluster: !!containingCluster});
                if (containingCluster) {
                    let originalMarker = null;
                    clustersLayer.eachLayer(l => {
                        if (l['_cluster'] === containingCluster) originalMarker = l;
                    });
                    activateCluster(containingCluster, originalMarker);
                }
            }
        }
        if (detailView && initialEventData && mapMarkers.length > 0) {
            const slug = initialEventData['slug'];
            const ev = slug ? mapMarkers.find(e => e['slug'] === slug ||
                (e['detail_url'] && e['detail_url'].endsWith('/' + slug + '/'))) : null;
            const evMarker = ev && ev['_marker'];
            if (evMarker && !(evMarker.isPopupOpen && evMarker.isPopupOpen())) {
                updateUrlForEvent(ev, true);
                const requestId = beginPopupRequest();
                const showPopup = () => {
                    if (!isCurrentPopupRequest(requestId)) {
                        return;
                    }
                    ensurePopupForMarker(evMarker, ev);
                    setTimeout(() => forceOpenMarkerPopup(evMarker, requestId), 300);
                };
                showPopup();
            }
        }
    }, [mapMarkers, mapClusters, map, eventsLayer, clustersLayer, createEventMarker, createClusterMarker, detailView, initialEventData, ensurePopupForMarker, updateUrlForEvent, activateCluster, beginPopupRequest, isCurrentPopupRequest, forceOpenMarkerPopup]);

    useEffect(() => {
        updateEventsList(allEventsData, !eventsDataFromFetchRef.current);
    }, [allEventsData]);

    const updateEventsList = (events, preserveOrder = false) => {
        if (!Array.isArray(events)) {
            console.error('updateEventsList: events is not an array:', events);
            return;
        }
        const sorted = preserveOrder ? [...events] : [...events].sort((a, b) => {
            if (userLocation && a.distance !== undefined && b.distance !== undefined) {
                return a.distance - b.distance;
            }
            const aStartAt = a && 'start_at' in a ? a['start_at'] : null;
            const bStartAt = b && 'start_at' in b ? b['start_at'] : null;
            if (aStartAt && bStartAt) {
                return new Date(aStartAt) - new Date(bStartAt);
            }
            return 0;
        });
        setCurrentSortedEvents(sorted);
        setDisplayedEventsCount(Math.min(LIST_BATCH_SIZE, sorted.length));
    };

    const buildEventsApiUrl = useCallback((timestamp = null, ongoingOnly = false, city = null) => {
        let apiUrl = '/api/events/?';
        const timezoneOffset = new Date().getTimezoneOffset() * -1;
        apiUrl += `timezone_offset=${timezoneOffset}`;
        if (timestamp) {
            apiUrl += `&timestamp=${timestamp}`;
        }
        if (ongoingOnly) {
            apiUrl += `&is_now=true`;
        }
        const cityKey = city !== null ? city : selectedCityRef.current;
        if (cityKey) {
            apiUrl += `&city=${encodeURIComponent(cityKey)}`;
        }
        apiUrl += `&language=${getCurrentLanguage()}`;
        return apiUrl;
    }, []);

    const loadEventsForDate = (timestamp = null, newPage = 1, ongoingOnly = showOngoingOnly, city = null) => {
        if (newPage === 1) {
            eventsDataFromFetchRef.current = true;
            setAllEventsData([]);
            setIsLoading(true);
            setShowSkeletons(true);
        } else {
            setLoadingMore(true);
        }
        const apiUrl = buildEventsApiUrl(timestamp, ongoingOnly, city);
        fetch(apiUrl)
            .then(response => {
                if (!response.ok) {
                    if (response.status === 429) {
                        return response.json().then(data => {
                            throw {isBlocked: true, data};
                        });
                    }
                    const error = new Error(`HTTP error! status: ${response.status}`);
                    return Promise.reject(error);
                }
                return response.json();
            })
            .then(data => {
                const blockedUntil = data && 'blocked_until' in data ? data['blocked_until'] : null;
                const hasError = data && data.error;
                if (hasError && blockedUntil) {
                    if (newPage === 1) {
                        setAllEventsData([]);
                    }
                } else if (hasError) {
                    if (newPage === 1) {
                        setAllEventsData([]);
                    }
                } else {
                    const events = Array.isArray(data?.events) ? data.events : (Array.isArray(data) ? data : []);
                    if (events.length > 0) {
                        const eventsWithDistances = userLocation
                            ? calculateDistances(userLocation[0], userLocation[1], events)
                            : events;
                        setAllEventsData(eventsWithDistances ?? events);
                    }
                }
                if (newPage === 1) {
                    setIsLoading(false);
                    setShowSkeletons(false);
                } else {
                    setLoadingMore(false);
                }
            })
            .catch(error => {
                eventsDataFromFetchRef.current = true;
                if (error.isBlocked && error.data) {
                    if (newPage === 1) {
                        setAllEventsData([]);
                    }
                } else {
                    console.error('Failed to load events:', error);
                }
                if (newPage === 1) {
                    setAllEventsData([]);
                    setIsLoading(false);
                    setShowSkeletons(false);
                } else {
                    setLoadingMore(false);
                }
            });
    };
    const changeDateFilter = (newTimestamp) => {
        const exitingDetailMode = detailView || (initialEventData && (initialEventsData && initialEventsData.length === 1 || allEventsData.length === 1));
        if (exitingDetailMode) {
            const lang = getCurrentLanguage();
            const basePath = lang === LANG_EN ? '/en/' : '/ru/';
            window.history.replaceState({}, '', basePath);
            if (isDetailModeActiveRef.current) {
                setIsDetailModeActive(false);
                isDetailModeActiveRef.current = false;
                setMapMarkers([]);
                setMapClusters([]);
            }
        }
        if (currentDateFilter !== newTimestamp || exitingDetailMode) {
            const shouldApplyOngoing = newTimestamp === null ? showOngoingOnly : false;
            if (newTimestamp !== null && showOngoingOnly) {
                setShowOngoingOnly(false);
            }
            setCurrentDateFilter(newTimestamp);
            setDisplayedEventsCount(0);
            setShowSkeletons(true);
            setIsFilterPanelOpen(false);
            resetSpiderfy();
            loadEventsForDate(newTimestamp, 1, shouldApplyOngoing);
            setTimeout(() => loadMapDataForViewport(), 0);
        }
    };
    changeDateFilterRef.current = changeDateFilter;
    useEffect(() => {
        const isDetailMode = detailView || (initialEventData && initialEventsData && initialEventsData.length === 1);
        if (isDetailMode) {
            return;
        }
        if (prevShowOngoingOnlyRef.current === showOngoingOnly) {
            prevShowOngoingOnlyRef.current = showOngoingOnly;
            return;
        }
        prevShowOngoingOnlyRef.current = showOngoingOnly;
        if (currentDateFilter === null) {
            setDisplayedEventsCount(0);
            setShowSkeletons(true);
            resetSpiderfy();
            loadEventsForDate(currentDateFilter, 1, showOngoingOnly);
            setTimeout(() => loadMapDataForViewport(), 0);
        }
    }, [showOngoingOnly, currentDateFilter, detailView, initialEventData, initialEventsData]);
    useEffect(() => {
        if (!isFilterPanelOpen) {
            return;
        }
        const handleClickOutside = (event) => {
            const panelEl = document.getElementById('ssr-filter-panel');
            const toggleEl = document.getElementById('ssr-filter-toggle');
            const chipEl = document.getElementById('city-chip');
            if (
                panelEl &&
                !panelEl.contains(event.target) &&
                (!toggleEl || !toggleEl.contains(event.target)) &&
                (!chipEl || !chipEl.contains(event.target))
            ) {
                setIsFilterPanelOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isFilterPanelOpen]);
    useEffect(() => {
        if (isInitializedRef.current) return;
        isInitializedRef.current = true;
        if (initialEventsData && Array.isArray(initialEventsData)) {
            console.log('[homeMap] Using SSR events data, count:', initialEventsData.length);
            updateEventsList(initialEventsData, true);
        }
    }, []);
    const zoomIn = () => {
        if (map) map.zoomIn();
    };
    const zoomOut = () => {
        if (map) map.zoomOut();
    };
    const locateUser = () => {
        if (navigator.geolocation) {
            setIsLoading(true);
            navigator.geolocation.getCurrentPosition(
                position => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;
                    updateUserLocationOnMap(lat, lng, {zoomLevel: 14});
                    fetch('/api/client-location/', {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({
                            latitude: lat,
                            longitude: lng,
                            accuracy: position.coords.accuracy,
                        }),
                    }).catch(error => {
                        console.error('Failed to store location:', error);
                    });
                    setIsLoading(false);
                },
                error => {
                    console.error('Geolocation failed:', error);
                    setIsLoading(false);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 300000
                }
            );
        }
    };
    const getViewportInsets = useCallback(() => {
        const insets = {top: 0, right: 0, bottom: 0, left: 0};
        const mapEl = mapRef.current;
        if (!mapEl) return insets;
        if (window.innerWidth >= 1024) return insets;
        const mapRect = mapEl.getBoundingClientRect();
        if (mapRect.height <= 0) return insets;
        const panel = document.getElementById('bottomPanel');
        if (panel) {
            if (panel.classList.contains('expanded')) {
                const panelRect = panel.getBoundingClientRect();
                insets.bottom = Math.max(insets.bottom, Math.max(0, mapRect.bottom - panelRect.top));
            } else {
                insets.bottom = Math.max(insets.bottom, 60);
            }
        }
        const footer = document.querySelector('.site-footer');
        if (footer) {
            const footerRect = footer.getBoundingClientRect();
            insets.bottom = Math.max(insets.bottom, Math.max(0, mapRect.bottom - footerRect.top));
        }
        return insets;
    }, []);
    const setViewAccountingForOverlays = useCallback((latlng, zoom = undefined, options = undefined) => {
        if (!map) return;
        const targetZoom = zoom != null ? zoom : (map.getZoom() || 0);
        const ll = Array.isArray(latlng)
            ? L.latLng(latlng[0], latlng[1])
            : (latlng instanceof L.LatLng ? latlng : L.latLng(latlng.lat, latlng.lng));
        const insets = getViewportInsets();
        const offsetX = (insets.left - insets.right) / 2;
        const offsetY = (insets.top - insets.bottom) / 2;
        if (offsetX === 0 && offsetY === 0) {
            map.setView(ll, targetZoom, options);
            return;
        }
        const point = map.project(ll, targetZoom).subtract(L.point(offsetX, offsetY));
        const adjusted = map.unproject(point, targetZoom);
        map.setView(adjusted, targetZoom, options);
    }, [map, getViewportInsets]);

    const handleEventClick = useCallback((event) => {
        const _center = map && map.getCenter ? map.getCenter() : null;
        const _bounds = map && map.getBounds ? map.getBounds() : null;
        console.log('[homeMap] handleEventClick', {
            slug: event && event['slug'],
            eventLat: event && event['latitude'],
            eventLon: event && event['longitude'],
            mapCenter: _center ? {lat: _center.lat, lng: _center.lng} : null,
            mapBounds: _bounds ? {south: _bounds.getSouth(), north: _bounds.getNorth(), west: _bounds.getWest(), east: _bounds.getEast()} : null,
            mapZoom: map && map.getZoom ? map.getZoom() : null,
        });
        if (window.innerWidth < 1024) {
            setIsExpanded(false);
            const panel = document.getElementById('bottomPanel');
            if (panel) panel.classList.remove('expanded');
        }
        updateUrlForEvent(event);
        const slug = getSlugFromEvent(event);
        if (!map) {
            console.log('[homeMap] handleEventClick: no map');
            return;
        }
        const requestId = beginPopupRequest();
        const eventLat = event && event['latitude'];
        const eventLon = event && event['longitude'];
        const eventName = event && event['name'];
        const eventDetailUrl = event && event['detail_url'];
        const coordsClose = (a, b) => Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < 1e-4;
        const matchesByCoords = (m) =>
            coordsClose(m['latitude'], eventLat) && coordsClose(m['longitude'], eventLon);
        const matchesByIdentity = (m) => {
            if (!m) return false;
            if (slug && (m['slug'] === slug || (m['detail_url'] && m['detail_url'].endsWith('/' + slug + '/')))) return true;
            if (eventDetailUrl && m['detail_url'] === eventDetailUrl) return true;
            if (eventName && m['name'] === eventName) return true;
            return false;
        };
        let existing = mapMarkersRef.current.find(matchesByIdentity) || mapMarkersRef.current.find(matchesByCoords);
        const marker = existing && existing['_marker'];
        console.log('[homeMap] handleEventClick: lookup', {slug, hasIndividualMarker: !!marker, mapMarkersCount: mapMarkersRef.current.length, mapClustersCount: (mapClustersRef.current || []).length});
        if (marker) {
            ensurePopupForMarker(marker, existing);
            forceOpenMarkerPopup(marker, requestId);
            setViewAccountingForOverlays(marker.getLatLng(), map.getZoom(), {animate: true, duration: 0.4});
            return;
        }
        const lat = event['latitude'];
        const lon = event['longitude'];
        const availableClusters = (mapClustersRef.current || []);
        const containingCluster = (lat != null && lon != null)
            ? availableClusters.find(cl => bboxContains(cl, lat, lon))
            : null;
        console.log('[homeMap] handleEventClick: cluster search', {eventLat: lat, eventLon: lon, foundCluster: !!containingCluster});
        if (containingCluster) {
            console.log('[homeMap] handleEventClick: standalone popup over cluster (no expand)');
            openStandalonePopupForEvent(event, [lat, lon], requestId);
            setViewAccountingForOverlays([lat, lon], map.getZoom(), {animate: true, duration: 0.4});
            return;
        }
        console.log('[homeMap] handleEventClick: fallback setView', {lat, lon});
        closeAllPopups();
        pendingPopupSlugRef.current = slug;
        pendingPopupMatchRef.current = matchesByIdentity;
        pendingPopupRequestIdRef.current = requestId;
        setViewAccountingForOverlays([lat, lon], map.getZoom(), {animate: true, duration: 0.4});
        fetchAndUpdatePopup(null, null);
    }, [map, eventsLayer, clustersLayer, ensurePopupForMarker, updateUrlForEvent, openStandalonePopupForEvent, getSlugFromEvent, setViewAccountingForOverlays, beginPopupRequest, forceOpenMarkerPopup, closeAllPopups]);
    handleEventClickRef.current = handleEventClick;

    useEffect(() => {
        if (!hasSSRPanel) return;
        const listItemsEl = document.getElementById('ssr-results-list-items');
        const panel = document.getElementById('bottomPanel');
        if (!listItemsEl || !panel) return;
        const ssrItemCount = listItemsEl.querySelectorAll('a.result-item').length;
        const countEl = panel.querySelector('.bottom-panel-count');
        if (countEl) countEl.textContent = String(currentSortedEvents.length);
        const ssrMarkupFrozen = ssrItemCount > 0 && !eventsDataFromFetchRef.current;
        if (!ssrMarkupFrozen && (isLoading || showSkeletons) && currentSortedEvents.length === 0) {
            renderedListRef.current = null;
            listItemsEl.innerHTML = LIST_SKELETON_HTML;
            return;
        }
        if (!ssrMarkupFrozen && currentSortedEvents.length === 0) {
            renderedListRef.current = null;
            listItemsEl.innerHTML = '<p style="text-align: center; color: #666; padding: 20px;">' + getMapLabels().noEvents + '</p>';
            return;
        }
        if (visibleEvents.length === 0) return;
        if (ssrMarkupFrozen && renderedListRef.current === null
            && matchesRenderedMarkup(listItemsEl, visibleEvents, ssrItemCount, getDetailUrl)) {
            renderedListRef.current = visibleEvents.slice(0, ssrItemCount);
        }
        const rendered = renderedListRef.current;
        const canAppend = isRenderedPrefixOf(rendered, visibleEvents);
        renderedListRef.current = visibleEvents;
        if (canAppend && rendered.length === visibleEvents.length) return;
        const listLang = getCurrentLanguage();
        const kmLabel = getMapLabels().km;
        const toHtml = (event) => buildResultItemHtml(event, {
            lang: listLang,
            href: getDetailUrl(event) || '#',
            kmLabel
        });
        if (canAppend) {
            listItemsEl.insertAdjacentHTML('beforeend', visibleEvents.slice(rendered.length).map(toHtml).join(''));
            return;
        }
        listItemsEl.innerHTML = visibleEvents.map(toHtml).join('');
    }, [hasSSRPanel, visibleEvents, currentSortedEvents, isLoading, showSkeletons, getDetailUrl, getMapLabels]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const listItemsEl = document.getElementById('ssr-results-list-items');
        if (!listItemsEl) return;
        const onListClick = (e) => {
            const link = e.target.closest('a.result-item');
            if (!link) return;
            e.preventDefault();
            const d = link.dataset;
            const titleEl = link.querySelector('.result-title');
            const eventData = {
                name: titleEl ? titleEl.textContent.trim() : '',
                detail_url: (link.getAttribute('href') || '').trim() || null,
                latitude: d.latitude != null && d.latitude !== '' ? parseFloat(d.latitude) : undefined,
                longitude: d.longitude != null && d.longitude !== '' ? parseFloat(d.longitude) : undefined,
                start_at: d.startAt || null,
                end_at: d.endAt || null,
                is_ongoing: d.isOngoing === 'true'
            };
            if (eventData.detail_url === '#') eventData.detail_url = null;
            const data = allEventsDataRef.current;
            const byDetailUrl = (ev) => eventData.detail_url && ev.detail_url && ev.detail_url === eventData.detail_url;
            const byName = (ev) => eventData.name && ev.name === eventData.name;
            const byCoords = (ev) => ev.latitude === eventData.latitude && ev.longitude === eventData.longitude;
            const event = data && (data.find(byDetailUrl) || data.find(byName) || data.find(byCoords));
            console.log('[homeMap] list click', {detail_url: eventData.detail_url, lat: eventData.latitude, lon: eventData.longitude, foundEvent: !!event, hasHandler: !!handleEventClickRef.current});
            if (event && handleEventClickRef.current) {
                handleEventClickRef.current(event);
            }
        };
        listItemsEl.addEventListener('click', onListClick);
        return () => listItemsEl.removeEventListener('click', onListClick);
    }, [hasSSRPanel]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const btn = document.getElementById('ssr-next-day-btn');
        if (!btn) return;
        const handler = () => {
            let nextTimestamp;
            if (currentDateFilterRef.current) {
                nextTimestamp = currentDateFilterRef.current + 86400000;
            } else {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(0, 0, 0, 0);
                nextTimestamp = tomorrow.getTime();
            }
            const fn = changeDateFilterRef.current;
            if (fn) fn(nextTimestamp);
        };
        btn.addEventListener('click', handler);
        return () => btn.removeEventListener('click', handler);
    }, [hasSSRPanel]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const btn = document.getElementById('ssr-next-day-btn');
        if (!btn) return;
        const busy = showSkeletons;
        btn.disabled = busy;
        btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    }, [hasSSRPanel, showSkeletons]);

    useEffect(() => {
        if (!hasSSRPanel || !map || !eventsLayer || !allEventsData.length) return;
        const findEvent = (data) => allEventsData.find(e =>
            (data.detail_url && e.detail_url && e.detail_url === data.detail_url) ||
            (e.latitude === data.latitude && e.longitude === data.longitude)
        );
        window.__openEventPopupOnMap = (eventData) => {
            const event = findEvent(eventData);
            if (event) {
                if (window.innerWidth < 1024) setIsExpanded(false);
                handleEventClick(event);
            }
        };
        return () => { delete window.__openEventPopupOnMap; };
    }, [hasSSRPanel, map, eventsLayer, allEventsData, handleEventClick]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const panel = document.getElementById('bottomPanel');
        if (!panel) {
            console.log('[homeMap] date-filter: bottomPanel not found');
            return;
        }
        console.log('[homeMap] date-filter: attaching click listener to panel', panel.id);
        const handler = (e) => {
            console.log('[homeMap] date-filter: panel click', { target: e.target?.className, tagName: e.target?.tagName });
            const btn = e.target.closest('.date-filter-btn');
            if (!btn) return;
            const container = btn.closest('.date-filter');
            if (!container) return;
            const buttons = container.querySelectorAll('.date-filter-btn');
            const index = Array.from(buttons).indexOf(btn);
            console.log('[homeMap] date-filter: button index', index, 'text=', btn.textContent?.trim());
            if (index === 0) {
                changeDateFilter(null);
            } else if (index === 1) {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(0, 0, 0, 0);
                changeDateFilter(tomorrow.getTime());
            }
        };
        panel.addEventListener('click', handler);
        return () => panel.removeEventListener('click', handler);
    }, [hasSSRPanel, changeDateFilter]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const panel = document.getElementById('bottomPanel');
        if (!panel) {
            console.log('[homeMap] date-picker: bottomPanel not found');
            return;
        }
        const pickerContainer = panel.querySelector('.date-picker-container');
        if (!pickerContainer) {
            console.log('[homeMap] date-picker: .date-picker-container not found');
            return;
        }
        console.log('[homeMap] date-picker: attaching click listener to panel');
        let dateInput = pickerContainer.querySelector('.date-picker-input');
        if (!dateInput) {
            dateInput = document.createElement('input');
            dateInput.type = 'date';
            dateInput.className = 'date-picker-input';
            dateInput.style.display = 'none';
            dateInput.addEventListener('change', () => {
                const fn = changeDateFilterRef.current;
                if (fn) applyDateFilterFromInput(dateInput.value ?? '', fn);
            });
            pickerContainer.appendChild(dateInput);
        }
        const pickerBtnHandler = (e) => {
            console.log('[homeMap] date-picker: panel click', { target: e.target?.className, closest: !!e.target?.closest?.('.date-picker-btn') });
            if (!e.target.closest('.date-picker-btn')) return;
            const input = panel.querySelector('.date-picker-input');
            if (input) input.showPicker();
        };
        panel.addEventListener('click', pickerBtnHandler);
        return () => {
            panel.removeEventListener('click', pickerBtnHandler);
        };
    }, [hasSSRPanel]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const panel = document.getElementById('bottomPanel');
        if (!panel) return;
        const container = panel.querySelector('.date-filter');
        if (!container) return;
        const buttons = container.querySelectorAll('.date-filter-btn');
        if (buttons.length < 2) return;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        const isTodayActive = currentDateFilter === null;
        const isTomorrowActive = currentDateFilter === tomorrow.getTime();
        buttons[0].classList.toggle('active', isTodayActive);
        buttons[1].classList.toggle('active', isTomorrowActive);
    }, [hasSSRPanel, currentDateFilter]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const panel = document.getElementById('bottomPanel');
        if (!panel) {
            console.log('[homeMap] filter-toggle: bottomPanel not found');
            return;
        }
        let toggle = panel.querySelector('.filter-toggle') || document.getElementById('ssr-filter-toggle');
        if (!toggle) {
            console.warn('[homeMap] filter-toggle: element not found (no .filter-toggle in panel, no #ssr-filter-toggle)');
            return;
        }
        console.log('[homeMap] filter-toggle: attaching click listener', { id: toggle.id, className: toggle.className });
        const handler = (e) => {
            console.log('[homeMap] filter-toggle: click', { target: e.target?.tagName, currentTarget: e.currentTarget?.id, isFilterPanelOpen });
            setIsFilterPanelOpen(prev => !prev);
        };
        toggle.addEventListener('click', handler);
        return () => toggle.removeEventListener('click', handler);
    }, [hasSSRPanel]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const applyFilterPanelVisibility = (open) => {
            const ssrPanel = document.getElementById('ssr-filter-panel');
            const backdrop = document.getElementById('ssr-filter-backdrop');
            const backdropInner = document.getElementById('ssr-filter-backdrop-inner');
            const hiddenDisplay = 'none';
            const visibleDisplay = '';
            if (ssrPanel) {
                ssrPanel.classList.toggle('filter-panel-ssr-hidden', !open);
                ssrPanel.style.display = open ? visibleDisplay : hiddenDisplay;
            }
            if (backdrop) {
                backdrop.classList.toggle('filter-panel-ssr-hidden', !open);
                backdrop.style.display = open ? visibleDisplay : hiddenDisplay;
                backdrop.style.pointerEvents = open ? visibleDisplay : 'none';
                backdrop.setAttribute('aria-hidden', String(!open));
            }
            if (backdropInner) {
                backdropInner.classList.toggle('filter-panel-ssr-hidden', !open);
                backdropInner.style.display = open ? visibleDisplay : hiddenDisplay;
                backdropInner.style.pointerEvents = open ? visibleDisplay : 'none';
                backdropInner.setAttribute('aria-hidden', String(!open));
            }
        };
        console.log('[homeMap] applyFilterPanelVisibility', isFilterPanelOpen);
        applyFilterPanelVisibility(isFilterPanelOpen);
        const panel = document.getElementById('bottomPanel');
        let toggle = panel ? panel.querySelector('.filter-toggle') : null;
        if (!toggle) toggle = document.getElementById('ssr-filter-toggle');
        if (toggle) {
            toggle.classList.toggle('active', isFilterPanelOpen);
            toggle.setAttribute('aria-expanded', String(isFilterPanelOpen));
        }
        const chip = document.getElementById('city-chip');
        if (chip) {
            chip.setAttribute('aria-expanded', String(isFilterPanelOpen));
        }
    }, [hasSSRPanel, isFilterPanelOpen]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        console.log('[homeMap] mount: forcing backdrops/panel hidden');
        const ssrPanel = document.getElementById('ssr-filter-panel');
        const backdrop = document.getElementById('ssr-filter-backdrop');
        const backdropInner = document.getElementById('ssr-filter-backdrop-inner');
        console.log('[homeMap] mount: elements found', { ssrPanel: !!ssrPanel, backdrop: !!backdrop, backdropInner: !!backdropInner });
        if (ssrPanel) {
            ssrPanel.classList.add('filter-panel-ssr-hidden');
            ssrPanel.style.display = 'none';
        }
        if (backdrop) {
            backdrop.classList.add('filter-panel-ssr-hidden');
            backdrop.style.display = 'none';
            backdrop.style.pointerEvents = 'none';
            backdrop.setAttribute('aria-hidden', 'true');
        }
        if (backdropInner) {
            backdropInner.classList.add('filter-panel-ssr-hidden');
            backdropInner.style.display = 'none';
            backdropInner.style.pointerEvents = 'none';
            backdropInner.setAttribute('aria-hidden', 'true');
        }
    }, [hasSSRPanel]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const ssrPanel = document.getElementById('ssr-filter-panel');
        const closeBtn = document.getElementById('ssr-filter-panel-close');
        if (!ssrPanel || !closeBtn) return;
        const handler = () => setIsFilterPanelOpen(false);
        closeBtn.addEventListener('click', handler);
        return () => closeBtn.removeEventListener('click', handler);
    }, [hasSSRPanel]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const checkbox = document.getElementById('ssr-ongoing-checkbox');
        const label = document.getElementById('ssr-ongoing-label');
        const hint = document.getElementById('ssr-ongoing-hint');
        if (!checkbox || !label) return;
        checkbox.checked = showOngoingOnly;
        const canUse = currentDateFilter === null;
        label.classList.toggle('disabled', !canUse);
        checkbox.disabled = !canUse;
        if (hint) hint.style.display = !canUse ? 'inline' : 'none';
        const handler = () => setShowOngoingOnly(checkbox.checked);
        checkbox.addEventListener('change', handler);
        return () => checkbox.removeEventListener('change', handler);
    }, [hasSSRPanel, showOngoingOnly, currentDateFilter]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const chip = document.getElementById('city-chip');
        const flag = document.getElementById('city-chip-flag');
        const name = document.getElementById('city-chip-name');
        if (!chip || !flag || !name) return;
        const labels = getMapLabels();
        const cityInfo = citiesData.find(c => c['key'] === selectedCity);
        if (cityInfo) {
            chip.classList.remove('city-chip--hint');
            flag.textContent = cityInfo['flag'] || '';
            name.textContent = cityInfo['name'] || '';
            chip.setAttribute('aria-label', `${labels.city || ''}: ${cityInfo['name'] || ''}`.trim());
            chip.removeAttribute('title');
            return;
        }
        if (ipCityName) {
            const hint = (labels.yourCityHint || '{city}').replace('{city}', ipCityName);
            chip.classList.add('city-chip--hint');
            flag.textContent = '📍';
            name.textContent = ipCityName;
            chip.setAttribute('aria-label', hint);
            chip.setAttribute('title', hint);
            return;
        }
        chip.classList.remove('city-chip--hint');
        flag.textContent = '';
        name.textContent = labels.allCities || '';
        chip.setAttribute('aria-label', labels.city || '');
        chip.removeAttribute('title');
    }, [hasSSRPanel, selectedCity, citiesData, ipCityName, getMapLabels]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const chip = document.getElementById('city-chip');
        if (!chip) return;
        const handler = () => {
            setIsFilterPanelOpen(true);
            if (!window.matchMedia('(min-width: 1024px)').matches) return;
            setTimeout(() => {
                const searchInput = document.getElementById('city-search-input');
                if (searchInput) searchInput.focus();
            }, 0);
        };
        chip.addEventListener('click', handler);
        return () => chip.removeEventListener('click', handler);
    }, [hasSSRPanel]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const cityPicker = document.getElementById('city-picker');
        const searchInput = document.getElementById('city-search-input');
        const clearBtn = document.getElementById('city-picker-clear');
        if (!cityPicker) return;
        const syncUI = (cityKey) => {
            cityPicker.querySelectorAll('.city-picker-item').forEach(item => {
                const isActive = item.dataset.cityKey === cityKey;
                item.classList.toggle('active', isActive);
                item.setAttribute('aria-selected', String(isActive));
                item.style.display = '';
            });
            const activeItem = cityPicker.querySelector(`.city-picker-item[data-city-key="${CSS.escape(cityKey)}"]`);
            if (searchInput) searchInput.value = activeItem ? (activeItem.dataset.cityName || '') : '';
            if (clearBtn) clearBtn.style.display = cityKey ? '' : 'none';
        };
        syncUI(selectedCity);
        const handler = (e) => {
            const item = e.target.closest('.city-picker-item');
            if (!item) return;
            const cityKey = item.dataset.cityKey;
            setSelectedCity(cityKey);
            localStorage.setItem('selectedCity', cityKey);
            const cityInfo = citiesData.find(c => c['key'] === cityKey);
            if (cityInfo && map) {
                applyInitialBoundsForLocation(cityInfo['center_lat'], cityInfo['center_lon'], cityKey, {
                    fallbackZoom: 11,
                    duration: 1.2,
                });
            }
            setDisplayedEventsCount(0);
            setShowSkeletons(true);
            loadEventsForDate(currentDateFilterRef.current, 1, showOngoingOnly, cityKey);
            syncUI(cityKey);
        };
        cityPicker.addEventListener('click', handler);
        return () => cityPicker.removeEventListener('click', handler);
    }, [hasSSRPanel, selectedCity, map, citiesData, showOngoingOnly, applyInitialBoundsForLocation]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const searchInput = document.getElementById('city-search-input');
        if (!searchInput) return;
        const handler = (e) => {
            const query = e.target.value.toLowerCase().trim();
            document.querySelectorAll('.city-picker-item').forEach(item => {
                const name = (item.dataset.cityName || '').toLowerCase();
                const country = (item.dataset.cityCountry || '').toLowerCase();
                const matches = !query || name.includes(query) || country.includes(query);
                item.style.display = matches ? '' : 'none';
            });
        };
        searchInput.addEventListener('input', handler);
        return () => searchInput.removeEventListener('input', handler);
    }, [hasSSRPanel]);

    useEffect(() => {
        if (!hasSSRPanel) return;
        const clearBtn = document.getElementById('city-picker-clear');
        const searchInput = document.getElementById('city-search-input');
        if (!clearBtn) return;
        const handler = () => {
            setSelectedCity('');
            localStorage.removeItem('selectedCity');
            if (searchInput) searchInput.value = '';
            clearBtn.style.display = 'none';
            document.querySelectorAll('.city-picker-item').forEach(item => {
                item.classList.remove('active');
                item.setAttribute('aria-selected', 'false');
                item.style.display = '';
            });
            setDisplayedEventsCount(0);
            setShowSkeletons(true);
            loadEventsForDate(currentDateFilterRef.current, 1, showOngoingOnly, '');
        };
        clearBtn.addEventListener('click', handler);
        return () => clearBtn.removeEventListener('click', handler);
    }, [hasSSRPanel, showOngoingOnly]);

    const loadMoreEvents = () => {
        const eventsToLoad = currentSortedEvents.slice(displayedEventsCount, displayedEventsCount + LIST_BATCH_SIZE);
        if (eventsToLoad.length > 0) {
            setDisplayedEventsCount(displayedEventsCount + eventsToLoad.length);
        }
    };
    useEffect(() => {
        if (!hasSSRPanel) return;
        const listElement = document.getElementById('ssr-results-list');
        if (!listElement) return;
        const handleScroll = () => {
            const {scrollTop, scrollHeight, clientHeight} = listElement;
            if (scrollHeight - scrollTop - clientHeight < 200 && !loadingMore) {
                if (displayedEventsCount < currentSortedEvents.length) {
                    setLoadingMore(true);
                    setTimeout(() => {
                        loadMoreEvents();
                        setLoadingMore(false);
                    }, 100);
                }
            }
        };
        listElement.addEventListener('scroll', handleScroll);
        return () => listElement.removeEventListener('scroll', handleScroll);
    }, [hasSSRPanel, displayedEventsCount, currentSortedEvents, loadingMore, detailView, initialEventData]);

    useEffect(() => {
        if (currentSortedEvents.length > 0 && displayedEventsCount === 0) {
            loadMoreEvents();
        }
    }, [currentSortedEvents]);

    useEffect(() => {
        if (!initialEventData || !map) {
            return;
        }
        const slug = initialEventData['slug'];
        if (!slug) return;
        const source = allEventsData.find(e => e['slug'] === slug || (e['detail_url'] && e['detail_url'].endsWith('/' + slug + '/')));
        const eventForUrl = source || initialEventData;
        updateUrlForEvent(eventForUrl, true);
        const requestId = beginPopupRequest();
        const showPopup = () => {
            if (!isCurrentPopupRequest(requestId)) {
                return;
            }
            const marker = mapMarkersRef.current.find(m => m['slug'] === slug || (m['detail_url'] && m['detail_url'].endsWith('/' + slug + '/')));
            const markerInstance = marker && marker['_marker'];
            if (markerInstance) {
                if (markerInstance.isPopupOpen && markerInstance.isPopupOpen()) {
                    return;
                }
                ensurePopupForMarker(markerInstance, marker);
                const targetZoom = Math.max(map.getZoom() || 0, 14);
                setViewAccountingForOverlays(markerInstance.getLatLng(), targetZoom);
                setTimeout(() => forceOpenMarkerPopup(markerInstance, requestId), 300);
                return;
            }
            pendingPopupSlugRef.current = slug;
            pendingPopupRequestIdRef.current = requestId;
            const lat = eventForUrl['latitude'] || (source && source['latitude']);
            const lng = eventForUrl['longitude'] || (source && source['longitude']);
            if (lat && lng) {
                setViewAccountingForOverlays([lat, lng], Math.max(map.getZoom() || 0, 14));
            }
        };
        showPopup();
    }, [initialEventData, allEventsData, map, ensurePopupForMarker, updateUrlForEvent, setViewAccountingForOverlays, beginPopupRequest, isCurrentPopupRequest, forceOpenMarkerPopup]);

    useEffect(() => {
        const handlePopState = () => {
            const path = window.location.pathname;
            const match = path.match(/^\/(ru|en)\/(sobytiya|events)\/([^/]+)\/?$/);
            if (match) {
                const pathSlug = match[3];
                if (!map) return;
                const marker = mapMarkersRef.current.find(m => m['slug'] === pathSlug || (m['detail_url'] && m['detail_url'].endsWith('/' + pathSlug + '/')));
                const markerInstance = marker && marker['_marker'];
                const requestId = beginPopupRequest();
                if (markerInstance) {
                    ensurePopupForMarker(markerInstance, marker);
                    const targetZoom = Math.max(map.getZoom() || 0, 14);
                    setViewAccountingForOverlays(markerInstance.getLatLng(), targetZoom);
                    setTimeout(() => forceOpenMarkerPopup(markerInstance, requestId), 300);
                    return;
                }
                const fallback = allEventsData.find(e => e['slug'] === pathSlug || (e['detail_url'] && e['detail_url'].endsWith('/' + pathSlug + '/')));
                if (fallback && fallback['latitude'] && fallback['longitude']) {
                    pendingPopupSlugRef.current = pathSlug;
                    pendingPopupRequestIdRef.current = requestId;
                    setViewAccountingForOverlays([fallback['latitude'], fallback['longitude']], Math.max(map.getZoom() || 0, 14));
                }
            } else {
                beginPopupRequest();
                closeAllPopups();
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [allEventsData, map, ensurePopupForMarker, setViewAccountingForOverlays, beginPopupRequest, forceOpenMarkerPopup, closeAllPopups]);

    useEffect(() => {
        const handleResize = () => {
            if (map) {
                map.invalidateSize();
            }
            if (window.innerWidth >= 1024) {
                setIsExpanded(true);
            }
        };
        window.addEventListener('resize', handleResize);
        setIsExpanded(true);
        return () => window.removeEventListener('resize', handleResize);
    }, [map]);
    useEffect(() => {
        if (!hasSSRPanel) return;
        const panel = document.getElementById('bottomPanel');
        if (panel) panel.dataset.expanded = String(isExpanded);
    }, [hasSSRPanel, isExpanded]);
    return (
        <>
            <div className="map-container">
                <div id="map" ref={mapRef}></div>
            </div>
            <button
                type="button"
                className="map-submit-event-btn"
                aria-label={getMapLabels().addEvent}
                onClick={() => setIsSubmitOpen(true)}
            >
                <i className="fas fa-pen" aria-hidden="true"></i>
                <span className="map-submit-event-btn__label">{getMapLabels().addEvent}</span>
            </button>
            <div className="map-controls">
                <button className="control-btn location" onClick={locateUser}>
                    <i className="fas fa-location-arrow"></i>
                </button>
                <button className="control-btn" onClick={zoomIn}>
                    <i className="fas fa-plus"></i>
                </button>
                <button className="control-btn" onClick={zoomOut}>
                    <i className="fas fa-minus"></i>
                </button>
            </div>
            {isLoading && (
                <div className="loading-overlay" id="loadingOverlay">
                    <div className="spinner"></div>
                </div>
            )}
            <SubmitEventModal
                isOpen={isSubmitOpen}
                onClose={() => setIsSubmitOpen(false)}
                map={map}
                labels={getMapLabels()}
                onExistingFound={(event) => {
                    if (map && event && Number.isFinite(event.latitude) && Number.isFinite(event.longitude)) {
                        map.flyTo([event.latitude, event.longitude], Math.max(map.getZoom(), 14));
                    }
                }}
            />
        </>
    );
};

const sendGlobalTrack = () => {
    fetch('/api/track/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
    }).catch(error => {
        console.error('Failed to send track:', error);
    });
};

const parseEventsFromResultItems = (containerEl) => {
    if (!containerEl) return [];
    const items = containerEl.querySelectorAll('.result-item');
    return Array.from(items).map(el => {
        const d = el.dataset;
        const titleEl = el.querySelector('.result-title');
        const name = titleEl ? titleEl.textContent.trim() : '';
        const timeSpan = el.querySelector('.result-time span');
        const display_time = timeSpan ? timeSpan.textContent.trim() : '';
        const href = el.getAttribute('href');
        const detail_url = (href && href !== '#') ? href : null;
        const lat = d.latitude != null && d.latitude !== '' ? parseFloat(d.latitude) : undefined;
        const lng = d.longitude != null && d.longitude !== '' ? parseFloat(d.longitude) : undefined;
        const tz = d.timezoneOffset != null && d.timezoneOffset !== '' ? parseInt(d.timezoneOffset, 10) : 0;
        const slugFromData = normalizeSlug(d.slug);
        const slugFromUrl = (() => {
            if (!detail_url) return null;
            const parts = detail_url.replace(/\/$/, '').split('/');
            return parts.length ? parts[parts.length - 1] : null;
        })();
        return {
            name,
            slug: slugFromData || slugFromUrl,
            detail_url,
            latitude: lat,
            longitude: lng,
            timezone_offset: tz,
            start_at: d.startAt || null,
            end_at: d.endAt || null,
            is_ongoing: d.isOngoing === 'true',
            display_time
        };
    });
};

const bootHomeMap = () => {
    const container = document.getElementById('home-map-root');
    if (!container) return;
    const listContainer = document.getElementById('ssr-results-list');
    const bottomPanel = document.getElementById('bottomPanel');
    const hasSSRPanel = !!(bottomPanel || listContainer);
    console.log('[homeMap] hasSSRPanel=', hasSSRPanel, '| bottomPanel=', !!bottomPanel, '| listContainer=', !!listContainer);
    const eventsDataElement = document.getElementById('initial-events-data');
    const eventsData = eventsDataElement ? (() => {
        try {
            const parsed = JSON.parse(eventsDataElement.textContent);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    })() : (listContainer ? parseEventsFromResultItems(listContainer) : parseEventsFromResultItems(container));
    const initialLocationElement = document.getElementById('initial-location-data');
    const initialLocation = initialLocationElement ? JSON.parse(initialLocationElement.textContent) : null;
    const mapConfigElement = document.getElementById('map-config-data');
    const mapConfig = mapConfigElement ? (() => {
        try {
            return JSON.parse(mapConfigElement.textContent) || {};
        } catch (e) {
            return {};
        }
    })() : {};
    const citiesElement = document.getElementById('cities-data');
    const citiesData = citiesElement ? (() => {
        try {
            return JSON.parse(citiesElement.textContent) || [];
        } catch (e) {
            return [];
        }
    })() : [];
    const initialEventElement = document.getElementById('initial-event-data');
    const initialEventData = initialEventElement ? (() => {
        try {
            const raw = initialEventElement.textContent.trim();
            return raw === 'null' ? null : JSON.parse(raw);
        } catch (e) {
            return null;
        }
    })() : null;
    const detailViewElement = document.getElementById('detail-view-data');
    const detailView = detailViewElement ? (() => {
        try {
            return JSON.parse(detailViewElement.textContent.trim());
        } catch (e) {
            return false;
        }
    })() : false;
    console.log('[homeMap] Starting with SSR data:', {
        eventsCount: eventsData.length,
        hasSSRPanel,
        bottomPanel: !!bottomPanel,
        listContainer: !!listContainer,
        initialLocation: initialLocation,
        initialEvent: initialEventData,
        detailView: detailView
    });
    const root = ReactDOM.createRoot(container);
    const doRender = (ssrPanel) => {
        root.render(<HomeMap initialEventsData={eventsData} initialLocation={initialLocation} initialEventData={initialEventData} detailView={detailView} hasSSRPanel={ssrPanel} citiesData={citiesData} mapConfig={mapConfig}/>);
    };
    doRender(hasSSRPanel);
    if (!hasSSRPanel) {
        setTimeout(() => {
            const bp = document.getElementById('bottomPanel');
            const lc = document.getElementById('ssr-results-list');
            if (bp || lc) {
                console.log('[homeMap] Panel found on delayed check, re-rendering with hasSSRPanel=true');
                doRender(true);
            }
        }, 150);
    }
};

document.addEventListener('DOMContentLoaded', () => {
    sendGlobalTrack();
    setInterval(sendGlobalTrack, 30000);
    bootHomeMap();
});
